import React, { useState, useEffect, useCallback, useRef, useMemo, startTransition } from "react";
import { flushSync } from "react-dom";
import {
	Container,
	Grid,
	Typography,
	Box,
	Paper,
	Pagination,
	CircularProgress,
	LinearProgress,
	Chip,
	MenuItem,
	Button,
	Stack,
	ThemeProvider,
	List,
	ListItem,
	ListItemText,
	Alert,
	Fade,
	Menu,
	IconButton,
	Tooltip,
	Popover,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormGroup,
	FormControlLabel,
	Checkbox,
	ToggleButtonGroup,
	ToggleButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import ProductCard from "./ProductCard/ProductCard";
import UploadPage from "./UploadPage/UploadPage";
import ColorContributionRamp, { colorContributionRowMapKey } from "./ColorContributionRamp";
import FashionabilityContributionRamp, {
	fashionabilityContributionRowKey,
	fashionabilityContributionMapKey,
} from "./FashionabilityContributionRamp";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import theme from "./theme";
import FilterDropdown from "./FilterDropdown";
import SizeRatioFilterDropdown from "./SizeRatioFilterDropdown";
import PictorialStPdfExportTooltipTitle from "./PdfExportTooltip";
import LoadingOverlay from "./LoadingOverlay";
import {
	SOH_COLOR_RAMP_ENABLED,
	getProductGpValue,
	sumGpValueForProducts,
	getProductBuyCogsValue,
	formatNumber,
	formatAvgRate,
	formatGPPercentage,
	formatGPValue,
	colorContributionMapKey,
	aggregateColorMetricContributions,
	aggregateColorSohContributions,
	aggregateFashionabilitySoldContributions,
	aggregateFashionabilityOverallContributions,
	sortProducts,
	filterKeyMap,
	PICTORIAL_CHANNEL_SOLD_DEF,
	channelSoldContributionPct,
} from "./pictorialUtils";
import {
	formatSizeLabel,
	getSizeLabelSortKey,
	productMatchesSizeFilter,
	getProductWhRatioKey,
	productMatchesWhRatioFilter,
	productMeetsWhRatioMinimum,
	compareWhRatioKeys,
} from "./sizeUtils";
import {
	PICTORIAL_SUMMARY_BOX_SX,
	PICTORIAL_SUMMARY_HEADER_SX,
	PICTORIAL_SUMMARY_ROW_SX,
	PICTORIAL_SUMMARY_SUBVALUE_SX,
	PICTORIAL_SUMMARY_STRIP_GRID_SX,
	PICTORIAL_SUMMARY_BAL_SX,
	PICTORIAL_CHANNEL_SOLD_GRID_SX,
	PICTORIAL_CHANNEL_SOLD_VALUE_SX,
} from "./summaryStyles";
import {
	FILTER_DEPS_DEBOUNCE_MS,
	fetchPictorialFilterOptions,
	fetchPictorialFilterDependencies,
	fetchPictorialCompanyTotals,
	fetchPictorialProducts,
	fetchPictorialKeyOptions,
	fetchPictorialBarcodeFacilitySoh,
	getProductExportPkey,
} from "./pictorialApi";
import {
	PICTORIAL_EXPORT_FIELDS,
	defaultExportFieldMask,
	defaultPdfTileFieldMask,
	hasTileExportFieldsSelected,
	hasCsvExportContentSelected,
	TILE_EXPORT_FIELD_IDS,
	EXPORT_CSV_FIELD_MAP,
	PICTORIAL_CSV_TOOLTIP_FIELDS,
	EXPORT_CSV_GROUP_FIELD_MAP,
	BARCODE_FACILITY_SOH_CSV_HEADERS,
	barcodeFacilitySohRowToCsvCells,
	preparePdfDomClone,
	waitForImagesInElement,
	escapeCsvCell,
	addPdfCoverHeader,
	pdfExportMenuSecondary,
} from "./exportUtils";

const PictorialST = () => {
	const [products, setProducts] = useState([]);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	/** Overlay copy: BigQuery round-trip vs client transform/pagination */
	const [dataLoadPhase, setDataLoadPhase] = useState(null);
	const [queryProgress, setQueryProgress] = useState(0);
	const [pdfGenerating, setPdfGenerating] = useState(false);
	const [, setPdfProgress] = useState(0);
	const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
	const [hoveredSort, setHoveredSort] = useState(null);
	const [currentPageDownloadAnchor, setCurrentPageDownloadAnchor] = useState(null);
	const [allPagesDownloadAnchor, setAllPagesDownloadAnchor] = useState(null);
	const [currentPageDownloadOpen, setCurrentPageDownloadOpen] = useState(false);
	const [allPagesDownloadOpen, setAllPagesDownloadOpen] = useState(false);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [pdfFieldPickerOpen, setPdfFieldPickerOpen] = useState(false);
	const [pdfFieldPickerScope, setPdfFieldPickerScope] = useState("current");
	const [exportPickerFormat, setExportPickerFormat] = useState("pdf");
	const [pdfFieldSelection, setPdfFieldSelection] = useState(() => defaultExportFieldMask());
	const pdfExportFieldMaskRef = useRef(defaultPdfTileFieldMask());
	const [dateRange, setDateRange] = useState({
		startDate: new Date(),
		endDate: new Date(),
	});
	const [pendingDateRange, setPendingDateRange] = useState({
		startDate: new Date(),
		endDate: new Date(),
	});
	const [totals, setTotals] = useState({
		totalBuy: 0,
		totalSold: 0,
		subCategory: "",
		avgASP: 0,
		avgMRP: 0,
		avgCOGS: 0,
		gpValue: 0,
		gpPercentage: 0,
		mrpValue: 0,
		soldMRPValue: 0,
		percentageSold: 0,
		salesContribution: 0,
		gpContribution: 0,
		buyCogsValue: 0,
		buyMulti: 0,
		soldCogsValue: 0,
		soldMulti: 0,
		totalSoldLifetime: 0,
		buyAvgCOGS: 0,
		avgMRPSold: 0,
		currentSoh: 0,
		stockCoverOneMonth: 0,
		netSalesValue: 0,
	});

	// State for company totals (without filters)
	const [companyTotals, setCompanyTotals] = useState({
		totalBuy: 0,
		totalSold: 0,
		totalSoldOverall: 0,
		balance: 0,
		periodStPct: 0,
		totalStPct: 0,
		gpValue: 0,
		mrpBuyValue: 0,
		mrpSoldValue: 0,
		rareRabbitSoldQty: 0,
		rareRabbitNetSalesValue: 0,
		rareRabbitGpValue: 0,
		innervOnlyBills: 0,
		innervWithOtherBills: 0,
	});
	const [pagination, setPagination] = useState({
		page: 1,
		per_page: 15,
		total_pages: 1,
		total: 0,
		isPageChange: false,
	});
	const [filters, setFilters] = useState({
		key: "",
		department: "",
		subCategory: "",
		brand: "",
		subBrand: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlend: "",
		nod: "",
		channel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
		aging: "",
		st_percentage: "",
		start_date: "",
		end_date: "",
	});

	const [, setPendingFilters] = useState({
		key: "",
		department: "",
		subCategory: "",
		brand: "",
		subBrand: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlend: "",
		nod: "",
		channel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
		aging: "",
		st_percentage: "",
		start_date: "",
		end_date: "",
	});

	const pdfRef = useRef(null);
	const [currentDateTime, setCurrentDateTime] = useState(new Date());
	const [filterOptions, setFilterOptions] = useState({
		keys: [],
		departments: [],
		subCategories: [],
		brands: [],
		subBrands: [],
		styles: [],
		colors: [],
		sleeves: [],
		divisions: [],
		seasons: [],
		fashionability: [],
		occasions: [],
		palettes: [],
		fitTypes: [],
		fabricBlends: [],
		nods: [],
		channels: [],
		stores: [],
		cities: [], // Add test cities
		states: [],
		regions: [],
		tiers: [],
		aging: [],
		st_percentage: [],
	});

	// Add state for master filter options
	const [masterFilterOptions, setMasterFilterOptions] = useState({});

	const [sortConfig, setSortConfig] = useState({
		field: "period_st",
		direction: "desc",
	});

	// Add new state for search terms and selected items
	const [searchTerms, setSearchTerms] = useState({
		key: "",
		brand: "",
		subBrand: "",
		department: "",
		subCategory: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlend: "",
		nod: "",
		channel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
	});

	const [selectedItems, setSelectedItems] = useState({
		keys: new Set(),
		brands: new Set(),
		subBrands: new Set(),
		departments: new Set(),
		subCategories: new Set(),
		styles: new Set(),
		colors: new Set(),
		sleeves: new Set(),
		divisions: new Set(),
		seasons: new Set(),
		fashionability: new Set(),
		occasions: new Set(),
		palettes: new Set(),
		fitTypes: new Set(),
		fabricBlends: new Set(),
		nods: new Set(),
		channels: new Set(),
		stores: new Set(),
		cities: new Set(),
		states: new Set(),
		regions: new Set(),
		tiers: new Set(),
	});

	// Add new state for all products
	const [allProducts, setAllProducts] = useState([]);
	/** Backend-resolved channel for ROS / Avg Aging (e.g. store's channel when Store filter is on). */
	const [metricsChannel, setMetricsChannel] = useState(null);
	// "WH" | "EBO" | null — client-side quick filters (only one group active at a time)
	const [brokenFilter, setBrokenFilter] = useState(null);
	const [availableFilter, setAvailableFilter] = useState(null);
	const [fullSizeSetFilter, setFullSizeSetFilter] = useState(null);
	/** Multi-select size labels (OR): e.g. ["32/S", "34/M"] */
	const [selectedSizeFilters, setSelectedSizeFilters] = useState([]);
	/** Multi-select WH size-ratio patterns (OR), e.g. "M 2, L 2, XL 1" */
	const [selectedSizeRatioFilters, setSelectedSizeRatioFilters] = useState(new Set());
	const [sizeRatioSearchTerm, setSizeRatioSearchTerm] = useState("");

	/** Colour ramp segment drilldown: grid shows only products in this bucket (toggle same segment to clear). */
	const [rampColorDrilldown, setRampColorDrilldown] = useState(null);
	/** Brief overlay on the product grid when a ramp colour is selected (UX feedback while the slice updates). */
	const [rampGridSelectionOverlay, setRampGridSelectionOverlay] = useState(false);
	const rampDrilldownRef = useRef(null);
	const rampGridOverlayTimeoutRef = useRef(null);
	rampDrilldownRef.current = rampColorDrilldown;

	// Add product cache for category filtering
	const [, setProductCache] = useState(new Map());

	// Helper function to check if any filters are active
	const hasActiveFilters = () => {
		return Object.values(filters).some((value) => value && value.trim() !== "");
	};

	// Add state for GP Contribution tooltip
	// GP Contribution card state
	const [gpContributionCard, setGpContributionCard] = useState({
		open: false,
		brandContributions: [],
		categoryContributions: [],
		colorContributions: [],
		colorContributionsTinw: [],
		colorContributionsSoh: [],
		selectedBrand: null,
		viewMode: "brands", // 'brands' | 'categories' | 'colors'
		colorViewFrom: null, // 'brands' | 'categories' — used when backing out of colors view
		transitioning: false,
	});
	const [gpContributionAnchorEl, setGpContributionAnchorEl] = useState(null);

	const closeGpContributionCard = useCallback(() => {
		setGpContributionCard((prev) => ({ ...prev, open: false }));
		setGpContributionAnchorEl(null);
	}, []);

	// Add new state for dashboard metrics

	// Add state to track open dropdowns
	const [openDropdowns, setOpenDropdowns] = useState(new Set());
	const prevOpenDropdownsSize = useRef(0);
	const filtersWhenDropdownOpened = useRef(null);

	// Add state for expand/collapse filters section
	const [filtersExpanded, setFiltersExpanded] = useState(true);

	// Add a ref for tracking the latest request
	const currentRequestIdRef = useRef(0);

	// Add a ref to track if we're in the middle of applying filters
	const isApplyingFiltersRef = useRef(false);

	// Add ref to track if filter options have been loaded
	const filterOptionsLoadedRef = useRef(false);

	// Add ref to track current filters to avoid dependency issues
	const filtersRef = useRef(filters);
	const filterDepsAbortRef = useRef(null);
	const filterDepsSeqRef = useRef(0);
	const filterDepsDebounceRef = useRef(null);

	// Update filtersRef whenever filters change
	useEffect(() => {
		filtersRef.current = filters;
	}, [filters]);

	// Function to calculate brand-wise GP contributions
	const calculateBrandContributions = useCallback((products) => {
		if (!products || products.length === 0) return [];

		const brandMap = new Map();

		products.forEach((product) => {
			const brand = product.brand || "Unknown";
			const gpValue = getProductGpValue(product);

			if (brandMap.has(brand)) {
				const existing = brandMap.get(brand);
				existing.gpValue += gpValue;
				existing.count += 1;
			} else {
				brandMap.set(brand, {
					brand,
					gpValue,
					count: 1,
				});
			}
		});

		const totalGPValue = Array.from(brandMap.values()).reduce((sum, item) => sum + item.gpValue, 0);

		return Array.from(brandMap.values())
			.map((item) => ({
				...item,
				percentage: totalGPValue > 0 ? Math.round((item.gpValue / totalGPValue) * 100 * 10) / 10 : 0,
			}))
			.filter((item) => item.gpValue !== 0)
			.sort((a, b) => b.gpValue - a.gpValue);
	}, []);

	// Function to calculate category-wise GP contributions within a selected brand
	const calculateCategoryContributions = useCallback((products, selectedBrand) => {
		if (!products || products.length === 0 || !selectedBrand) {
			return [];
		}

		// Filter products for the selected brand (case-insensitive)
		const brandProducts = products.filter((product) => (product.brand || "Unknown").toLowerCase() === selectedBrand.toLowerCase());

		if (brandProducts.length === 0) {
			return [];
		}

		const categoryMap = new Map();

		brandProducts.forEach((product) => {
			const category = product.category || "Unknown";
			const gpValue = getProductGpValue(product);

			if (categoryMap.has(category)) {
				const existing = categoryMap.get(category);
				existing.gpValue += gpValue;
				existing.count += 1;
			} else {
				categoryMap.set(category, {
					category,
					gpValue,
					count: 1,
				});
			}
		});

		// Calculate total GP value for this brand (should be 100% of brand's contribution)
		const brandTotalGPValue = Array.from(categoryMap.values()).reduce((sum, item) => sum + item.gpValue, 0);

		return Array.from(categoryMap.values())
			.map((item) => ({
				...item,
				percentage: brandTotalGPValue > 0 ? Math.round((item.gpValue / brandTotalGPValue) * 100 * 10) / 10 : 0,
			}))
			.filter((item) => item.gpValue !== 0)
			.sort((a, b) => b.gpValue - a.gpValue);
	}, []);

	const calculateColorContributions = useCallback(
		(products, brandFilter = null) =>
			aggregateColorMetricContributions(
				products,
				brandFilter,
				(p) => p.troco_ls?.t_sale,
				(p) => p.lcogs?.gp_value
			),
		[]
	);

	const calculateColorContributionsOverall = useCallback(
		(products, brandFilter = null) =>
			aggregateColorMetricContributions(
				products,
				brandFilter,
				(p) => p.troco_ls?.t_sale_total,
				(p) => p.lcogs?.gp_value
			),
		[]
	);

	const calculateColorContributionsTinw = useCallback(
		(products, brandFilter = null) =>
			aggregateColorMetricContributions(
				products,
				brandFilter,
				(p) => p.troco_ls?.t_inw,
				getProductBuyCogsValue
			),
		[]
	);

	const calculateColorContributionsSoh = useCallback(
		(products, brandFilter = null) => aggregateColorSohContributions(products, brandFilter),
		[]
	);

	const calculateFashionabilityContributions = useCallback(
		(products, brandFilter = null) => aggregateFashionabilitySoldContributions(products, brandFilter),
		[]
	);

	const calculateFashionabilityContributionsOverall = useCallback(
		(products, brandFilter = null) => aggregateFashionabilityOverallContributions(products, brandFilter),
		[]
	);

	const colorContributionRowsLoaded = useMemo(
		() => calculateColorContributions(allProducts, null),
		[allProducts, calculateColorContributions]
	);

	const colorContributionRowsOverallLoaded = useMemo(
		() => calculateColorContributionsOverall(allProducts, null),
		[allProducts, calculateColorContributionsOverall]
	);

	const colorContributionRowsTinwLoaded = useMemo(
		() => calculateColorContributionsTinw(allProducts, null),
		[allProducts, calculateColorContributionsTinw]
	);

	const colorContributionRowsSohLoaded = useMemo(
		() => (SOH_COLOR_RAMP_ENABLED ? calculateColorContributionsSoh(allProducts, null) : []),
		[allProducts, calculateColorContributionsSoh]
	);

	const fashionabilityContributionRowsLoaded = useMemo(
		() => calculateFashionabilityContributions(allProducts, null),
		[allProducts, calculateFashionabilityContributions]
	);

	const fashionabilityContributionRowsOverallLoaded = useMemo(
		() => calculateFashionabilityContributionsOverall(allProducts, null),
		[allProducts, calculateFashionabilityContributionsOverall]
	);

	const allProductsForView = useMemo(() => {
		if (!rampColorDrilldown?.key) return allProducts;
		if (
			rampColorDrilldown.dimension === "fashionability" ||
			rampColorDrilldown.dimension === "fashionability_overall"
		) {
			return allProducts.filter((p) => fashionabilityContributionMapKey(p) === rampColorDrilldown.key);
		}
		return allProducts.filter((p) => colorContributionMapKey(p) === rampColorDrilldown.key);
	}, [allProducts, rampColorDrilldown]);

	const getPivotalSizeRows = useCallback((p) =>
		Array.isArray(p.size_soh_breakdown)
			? p.size_soh_breakdown.filter((r) => r.pivotal || r.is_pivotal)
			: [], []);

	const getAllSizeRows = useCallback((p) =>
		Array.isArray(p.size_soh_breakdown) ? p.size_soh_breakdown : [], []);

	/** Returns true when any pivotal size has 0 WH SOH */
	const isProductWhBroken = useCallback((p) => {
		const pivRows = getPivotalSizeRows(p);
		return pivRows.length > 0 && pivRows.some((r) => (r.wh_soh || 0) === 0);
	}, [getPivotalSizeRows]);

	/** Returns true when any pivotal size has 0 EBO SOH */
	const isProductEboBroken = useCallback((p) => {
		const pivRows = getPivotalSizeRows(p);
		return pivRows.length > 0 && pivRows.some((r) => (r.ebo_soh || 0) === 0);
	}, [getPivotalSizeRows]);

	/** All pivotal sizes have WH SOH > 0 */
	const isProductWhPivotalAvailable = useCallback((p) => {
		const pivRows = getPivotalSizeRows(p);
		if (pivRows.length === 0) return (p.pivotal_wh_soh || 0) > 0;
		return pivRows.every((r) => (r.wh_soh || 0) > 0);
	}, [getPivotalSizeRows]);

	/** All pivotal sizes have EBO SOH > 0 */
	const isProductEboPivotalAvailable = useCallback((p) => {
		const pivRows = getPivotalSizeRows(p);
		if (pivRows.length === 0) return (p.pivotal_ebo_soh || 0) > 0;
		return pivRows.every((r) => (r.ebo_soh || 0) > 0);
	}, [getPivotalSizeRows]);

	/** Every size in the curve has WH SOH > 0 */
	const isProductWhFullSizeSet = useCallback((p) => {
		const rows = getAllSizeRows(p);
		return rows.length > 0 && rows.every((r) => (r.wh_soh || 0) > 0);
	}, [getAllSizeRows]);

	/** Every size in the curve has EBO SOH > 0 */
	const isProductEboFullSizeSet = useCallback((p) => {
		const rows = getAllSizeRows(p);
		return rows.length > 0 && rows.every((r) => (r.ebo_soh || 0) > 0);
	}, [getAllSizeRows]);

	const clearOtherQuickFilters = useCallback((keep) => {
		if (keep !== "broken") setBrokenFilter(null);
		if (keep !== "available") setAvailableFilter(null);
		if (keep !== "fullSizeSet") setFullSizeSetFilter(null);
	}, []);

	const handleBrokenFilterChange = useCallback(
		(_, val) => {
			setBrokenFilter(val);
			if (val) clearOtherQuickFilters("broken");
		},
		[clearOtherQuickFilters]
	);

	const handleAvailableFilterChange = useCallback(
		(_, val) => {
			setAvailableFilter(val);
			if (val) clearOtherQuickFilters("available");
		},
		[clearOtherQuickFilters]
	);

	const handleFullSizeSetFilterChange = useCallback(
		(_, val) => {
			setFullSizeSetFilter(val);
			if (val) clearOtherQuickFilters("fullSizeSet");
		},
		[clearOtherQuickFilters]
	);

	const activeQuickFilter = brokenFilter || availableFilter || fullSizeSetFilter;

	const quickFilterExportLabel = useMemo(() => {
		if (brokenFilter === "WH") return "Pivotal Broken — WH Broken";
		if (brokenFilter === "EBO") return "Pivotal Broken — EBO Broken";
		if (availableFilter === "WH") return "Pivotal Available — WH Available";
		if (availableFilter === "EBO") return "Pivotal Available — EBO Available";
		if (fullSizeSetFilter === "WH") return "Full Size Set — WH Full Set";
		if (fullSizeSetFilter === "EBO") return "Full Size Set — EBO Full Set";
		return null;
	}, [brokenFilter, availableFilter, fullSizeSetFilter]);

	// Counts across the FULL result set (not just current page)
	const whBrokenCount  = useMemo(() => allProductsForView.filter(isProductWhBroken).length,  [allProductsForView, isProductWhBroken]);
	const eboBrokenCount = useMemo(() => allProductsForView.filter(isProductEboBroken).length, [allProductsForView, isProductEboBroken]);
	const whAvailableCount  = useMemo(() => allProductsForView.filter(isProductWhPivotalAvailable).length,  [allProductsForView, isProductWhPivotalAvailable]);
	const eboAvailableCount = useMemo(() => allProductsForView.filter(isProductEboPivotalAvailable).length, [allProductsForView, isProductEboPivotalAvailable]);
	const whFullSizeSetCount  = useMemo(() => allProductsForView.filter(isProductWhFullSizeSet).length,  [allProductsForView, isProductWhFullSizeSet]);
	const eboFullSizeSetCount = useMemo(() => allProductsForView.filter(isProductEboFullSizeSet).length, [allProductsForView, isProductEboFullSizeSet]);

	const availableSizeOptions = useMemo(() => {
		const counts = new Map();
		for (const product of allProductsForView) {
			const labels = new Set();
			const rows = Array.isArray(product.size_soh_breakdown) ? product.size_soh_breakdown : [];
			for (const row of rows) {
				const label = formatSizeLabel(row.size || row.size_label);
				if (label && label !== "?") labels.add(label);
			}
			for (const label of labels) {
				counts.set(label, (counts.get(label) || 0) + 1);
			}
		}
		return Array.from(counts.entries())
			.map(([label, count]) => ({ label, count }))
			.sort((a, b) => {
				const rankDiff = getSizeLabelSortKey(a.label) - getSizeLabelSortKey(b.label);
				return rankDiff !== 0 ? rankDiff : a.label.localeCompare(b.label);
			});
	}, [allProductsForView]);

	const availableSizeRatioOptions = useMemo(() => {
		const ratioKeys = new Set();
		for (const product of allProductsForView) {
			const key = getProductWhRatioKey(product);
			if (key) ratioKeys.add(key);
		}
		return Array.from(ratioKeys)
			.map((key) => ({
				key,
				count: allProductsForView.filter((p) => productMeetsWhRatioMinimum(p, key)).length,
			}))
			.sort((a, b) => compareWhRatioKeys(a.key, b.key));
	}, [allProductsForView]);

	const handleSizeFilterChange = useCallback((_, newSizes) => {
		setSelectedSizeFilters(newSizes || []);
	}, []);

	// Apply quick + size filters BEFORE pagination so they work across all pages
	const paginationSourceList = useMemo(() => {
		let list = allProductsForView;
		if (brokenFilter === "WH") list = list.filter(isProductWhBroken);
		else if (brokenFilter === "EBO") list = list.filter(isProductEboBroken);
		else if (availableFilter === "WH") list = list.filter(isProductWhPivotalAvailable);
		else if (availableFilter === "EBO") list = list.filter(isProductEboPivotalAvailable);
		else if (fullSizeSetFilter === "WH") list = list.filter(isProductWhFullSizeSet);
		else if (fullSizeSetFilter === "EBO") list = list.filter(isProductEboFullSizeSet);
		if (selectedSizeFilters.length > 0) {
			list = list.filter((p) => productMatchesSizeFilter(p, selectedSizeFilters));
		}
		if (selectedSizeRatioFilters.size > 0) {
			list = list.filter((p) =>
				productMatchesWhRatioFilter(p, Array.from(selectedSizeRatioFilters))
			);
		}
		return list;
	}, [
		allProductsForView,
		brokenFilter,
		availableFilter,
		fullSizeSetFilter,
		selectedSizeFilters,
		selectedSizeRatioFilters,
		isProductWhBroken,
		isProductEboBroken,
		isProductWhPivotalAvailable,
		isProductEboPivotalAvailable,
		isProductWhFullSizeSet,
		isProductEboFullSizeSet,
	]);

	const activeSizeFilter = selectedSizeFilters.length > 0;
	const activeSizeRatioFilter = selectedSizeRatioFilters.size > 0;

	/** Multi-page export uses filtered list when a stock or size quick filter is selected */
	const productsForAllPagesExport =
		activeQuickFilter || activeSizeFilter || activeSizeRatioFilter ? paginationSourceList : allProducts;

	const clearRampGridOverlayTimeout = useCallback(() => {
		if (rampGridOverlayTimeoutRef.current != null) {
			clearTimeout(rampGridOverlayTimeoutRef.current);
			rampGridOverlayTimeoutRef.current = null;
		}
	}, []);

	const handleRampSegmentClick = useCallback(
		(row, dimension = "color") => {
			const isFashionabilityRamp =
				dimension === "fashionability" || dimension === "fashionability_overall";
			const key = isFashionabilityRamp
				? fashionabilityContributionRowKey(row)
				: colorContributionRowMapKey(row);
			const label = isFashionabilityRamp
				? String(row?.fashionability ?? "").trim() || "Fashionability"
				: String(row?.color ?? "").trim() || "Colour";
			const prev = rampDrilldownRef.current;
			const prevDimension = prev?.dimension ?? "color";
			const clearing = prev?.key === key && prevDimension === dimension;
			clearRampGridOverlayTimeout();
			setRampColorDrilldown(clearing ? null : { key, label, dimension });
			if (clearing) {
				setRampGridSelectionOverlay(false);
			} else {
				setRampGridSelectionOverlay(true);
				rampGridOverlayTimeoutRef.current = setTimeout(() => {
					rampGridOverlayTimeoutRef.current = null;
					setRampGridSelectionOverlay(false);
				}, 320);
			}
		},
		[clearRampGridOverlayTimeout]
	);

	const rampColorSelectedKey =
		rampColorDrilldown?.dimension === "color" || !rampColorDrilldown?.dimension
			? rampColorDrilldown?.key ?? null
			: null;
	const rampColorOverallSelectedKey =
		rampColorDrilldown?.dimension === "color_overall" ? rampColorDrilldown?.key ?? null : null;
	const rampFashionabilitySelectedKey =
		rampColorDrilldown?.dimension === "fashionability" ? rampColorDrilldown?.key ?? null : null;
	const rampFashionabilityOverallSelectedKey =
		rampColorDrilldown?.dimension === "fashionability_overall" ? rampColorDrilldown?.key ?? null : null;

	const prevRampFilterKeyRef = useRef(null);
	const prevQuickFilterRef = useRef(null);
	// Holds the PKEY we need to scroll-to after a page change renders
	const pendingScrollPkeyRef = useRef(null);
	/** When jumping from WH/EBO broken view to a non-broken card: target page in full list after filter clears */
	const pendingScrollJumpRef = useRef(null);

	useEffect(() => {
		if (rampColorDrilldown) return;
		clearRampGridOverlayTimeout();
		setRampGridSelectionOverlay(false);
	}, [rampColorDrilldown, clearRampGridOverlayTimeout]);

	useEffect(() => {
		return () => clearRampGridOverlayTimeout();
	}, [clearRampGridOverlayTimeout]);

	useEffect(() => {
		const list = paginationSourceList;
		const per = pagination.per_page;
		const tp = Math.max(1, Math.ceil(list.length / per));
		const currentKey = rampColorDrilldown
			? `${rampColorDrilldown.dimension ?? "color"}:${rampColorDrilldown.key}`
			: null;
		const rampKeyChanged = prevRampFilterKeyRef.current !== currentKey;
		prevRampFilterKeyRef.current = currentKey;
		const quickFilterKey = `${brokenFilter || ""}|${availableFilter || ""}|${fullSizeSetFilter || ""}|${selectedSizeFilters.join(",")}|${Array.from(selectedSizeRatioFilters).join(";")}`;
		const quickFilterChanged = prevQuickFilterRef.current !== quickFilterKey;
		prevQuickFilterRef.current = quickFilterKey;
		const nextPage = (rampKeyChanged || quickFilterChanged) ? 1 : Math.min(pagination.page, tp);
		let safePage = Math.min(nextPage, tp);
		const jump = pendingScrollJumpRef.current;
		if (jump?.pkey != null && jump.page != null && list.some((p) => p.PKEY === jump.pkey)) {
			safePage = Math.min(Math.max(1, jump.page), tp);
			pendingScrollJumpRef.current = null;
		}
		const start = (safePage - 1) * per;
		setPagination((prev) => ({
			...prev,
			total: list.length,
			total_pages: tp,
			page: safePage,
		}));
		setProducts(list.slice(start, start + per));
	}, [
		paginationSourceList,
		rampColorDrilldown,
		brokenFilter,
		availableFilter,
		fullSizeSetFilter,
		selectedSizeFilters,
		selectedSizeRatioFilters,
		pagination.per_page,
		pagination.page,
	]);

	const openColorContributionView = useCallback(
		(fromView, brandForFilter) => {
			const brandArg = fromView === "categories" ? brandForFilter : null;
			const colorContributions = calculateColorContributions(allProducts, brandArg);
			const colorContributionsTinw = calculateColorContributionsTinw(allProducts, brandArg);
			const colorContributionsSoh = SOH_COLOR_RAMP_ENABLED
				? calculateColorContributionsSoh(allProducts, brandArg)
				: [];
			setGpContributionCard((prev) => ({ ...prev, transitioning: true }));
			setTimeout(() => {
				setGpContributionCard((prev) => ({
					...prev,
					colorContributions,
					colorContributionsTinw,
					colorContributionsSoh,
					viewMode: "colors",
					colorViewFrom: fromView,
					transitioning: false,
				}));
			}, 150);
		},
		[allProducts, calculateColorContributions, calculateColorContributionsTinw, calculateColorContributionsSoh]
	);

	const handleBackFromColors = useCallback(() => {
		setGpContributionCard((prev) => ({ ...prev, transitioning: true }));
		setTimeout(() => {
			setGpContributionCard((prev) => ({
				...prev,
				viewMode: prev.colorViewFrom || "brands",
				colorContributions: [],
				colorContributionsTinw: [],
				colorContributionsSoh: [],
				colorViewFrom: null,
				transitioning: false,
			}));
		}, 150);
	}, []);

	// GP Contribution card handlers
	const handleGpContributionClick = useCallback(
		(event) => {
			event.stopPropagation();

			// Toggle card if clicking on the same element
			if (gpContributionCard.open) {
				closeGpContributionCard();
				return;
			}

			setGpContributionAnchorEl(event.currentTarget);

			// Check how many brands are selected
			const selectedBrands = selectedItems.brands || new Set();
			const selectedBrandCount = selectedBrands.size;

			// If single brand is selected, go directly to category split level
			if (selectedBrandCount === 1) {
				const selectedBrand = Array.from(selectedBrands)[0];
				const categoryContributions = calculateCategoryContributions(allProducts, selectedBrand);

				setGpContributionCard({
					open: true,
					brandContributions: [],
					categoryContributions: categoryContributions,
					colorContributions: [],
					colorContributionsTinw: [],
					colorContributionsSoh: [],
					selectedBrand: selectedBrand,
					viewMode: "categories",
					colorViewFrom: null,
					transitioning: false,
				});
			} else {
				// If multiple brands or no brands selected, start with brand view
				const brandContributions = calculateBrandContributions(allProducts);
				setGpContributionCard({
					open: true,
					brandContributions: brandContributions,
					categoryContributions: [],
					colorContributions: [],
					colorContributionsTinw: [],
					colorContributionsSoh: [],
					selectedBrand: null,
					viewMode: "brands",
					colorViewFrom: null,
					transitioning: false,
				});
			}
		},
		[
			allProducts,
			calculateBrandContributions,
			calculateCategoryContributions,
			gpContributionCard.open,
			selectedItems.brands,
			closeGpContributionCard,
		]
	);

	const handleBrandClick = useCallback(
		(brand) => {
			// Show category split for the selected brand
			const categoryContributions = calculateCategoryContributions(allProducts, brand);

			// Start transition
			setGpContributionCard((prev) => ({
				...prev,
				transitioning: true,
			}));

			// After transition delay, switch to category view
			setTimeout(() => {
				setGpContributionCard((prev) => ({
					...prev,
					categoryContributions: categoryContributions,
					colorContributions: [],
					colorContributionsTinw: [],
					colorContributionsSoh: [],
					colorViewFrom: null,
					selectedBrand: brand,
					viewMode: "categories",
					transitioning: false,
				}));
			}, 150); // Half of the transition duration
		},
		[allProducts, calculateCategoryContributions]
	);

	const handleBackToBrands = useCallback(() => {
		// Start transition
		setGpContributionCard((prev) => ({
			...prev,
			transitioning: true,
		}));

		// After transition delay, switch to brand view
		setTimeout(() => {
			const brandContributions = calculateBrandContributions(allProducts);
			setGpContributionCard((prev) => ({
				...prev,
				brandContributions: brandContributions,
				categoryContributions: [],
				colorContributions: [],
				colorContributionsTinw: [],
				colorContributionsSoh: [],
				colorViewFrom: null,
				selectedBrand: null,
				viewMode: "brands",
				transitioning: false,
			}));
		}, 150); // Half of the transition duration
	}, [allProducts, calculateBrandContributions]);

	// Global flag to prevent duplicate calls in StrictMode
	window.filterOptionsLoaded = window.filterOptionsLoaded || false;

	// Cache for filter dependencies to prevent duplicate calls

	// Global flag to prevent duplicate filter-options calls across components
	window.filterOptionsCallInProgress = window.filterOptionsCallInProgress || false;

	const fetchFilterOptions = useCallback(async (startDate, endDate) => {
		if (filterOptionsLoadedRef.current || window.filterOptionsLoaded) {
			return;
		}

		// Prevent concurrent calls
		if (window.filterOptionsCallInProgress) {
			return;
		}

		try {
			setFilterOptionsLoading(true);
			window.filterOptionsCallInProgress = true;

			const filterData = await fetchPictorialFilterOptions(startDate, endDate);

			console.log("Setting filter options with data:", filterData);

			setFilterOptions((prev) => ({
				...prev,
				...filterData,
			}));

			setMasterFilterOptions(filterData);

			filterOptionsLoadedRef.current = true;
			window.filterOptionsLoaded = true;
		} catch (err) {
			console.error("Error fetching filter options:", err);
			setError("Failed to load filter options. Please refresh the page.");
		} finally {
			setFilterOptionsLoading(false);
			window.filterOptionsCallInProgress = false;
		}
	}, []);

	const mapFilterDependenciesToOptions = useCallback((data) => {
		const master = masterFilterOptions || {};
		return {
			departments: data.departments || [],
			subCategories: data.subCategories || [],
			brands: data.brands || [],
			subBrands: data.subBrands || [],
			styles: data.styles || [],
			colors: data.colors || [],
			sleeves: data.sleeves || [],
			divisions: data.divisions || [],
			seasons: data.seasons || [],
			fashionability: data.fashionability || [],
			occasions: data.occasions || [],
			palettes: data.palettes || [],
			fitTypes: data.fitTypes || [],
			fabricBlends: data.fabricBlends || [],
			nods: data.nods || master.nods || [],
			channels: data.channels || [],
			stores: data.stores || [],
			cities: data.cities || [],
			states: data.states || [],
			regions: data.regions || [],
			tiers: data.tiers || [],
			aging: master.aging || filterOptions.aging || [],
			st_percentage: master.st_percentage || filterOptions.st_percentage || [],
		};
	}, [masterFilterOptions, filterOptions.aging, filterOptions.st_percentage]);

	const fetchFilterDependencies = useCallback(async (activeFilters) => {
		if (!filterOptionsLoadedRef.current) return;

		if (filterDepsAbortRef.current) {
			filterDepsAbortRef.current.abort();
		}
		const abortController = new AbortController();
		filterDepsAbortRef.current = abortController;
		const seq = ++filterDepsSeqRef.current;

		try {
			const data = await fetchPictorialFilterDependencies(activeFilters, abortController.signal);
			if (seq !== filterDepsSeqRef.current) return;

			const narrowed = mapFilterDependenciesToOptions(data);
			setFilterOptions((prev) => ({ ...prev, ...narrowed }));

			const fieldToOptionsKey = {
				department: "departments",
				subCategory: "subCategories",
				brand: "brands",
				subBrand: "subBrands",
				style: "styles",
				color: "colors",
				sleeve: "sleeves",
				division: "divisions",
				season: "seasons",
				fashionability: "fashionability",
				occasion: "occasions",
				palette: "palettes",
				fitType: "fitTypes",
				fabricBlend: "fabricBlends",
				nod: "nods",
				channel: "channels",
				store: "stores",
				city: "cities",
				state: "states",
				region: "regions",
				tier: "tiers",
			};

			setSelectedItems((prev) => {
				const next = { ...prev };
				let changed = false;
				Object.entries(fieldToOptionsKey).forEach(([field, optionsKey]) => {
					const allowed = new Set(narrowed[optionsKey] || []);
					if (allowed.size === 0) return;
					const current = prev[field] || new Set();
					const pruned = new Set([...current].filter((v) => allowed.has(v)));
					if (pruned.size !== current.size) {
						changed = true;
						next[field] = pruned;
					}
				});
				if (!changed) return prev;

				const updatedFilters = { ...filtersRef.current };
				Object.entries(fieldToOptionsKey).forEach(([field]) => {
					const setVal = next[field];
					updatedFilters[field] = setVal && setVal.size > 0 ? Array.from(setVal).join(",") : "";
				});
				setFilters(updatedFilters);
				return next;
			});
		} catch (err) {
			if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
			console.error("Error fetching filter dependencies:", err);
		}
	}, [mapFilterDependenciesToOptions]);

	const scheduleFilterDependencies = useCallback(
		(activeFilters) => {
			if (filterDepsDebounceRef.current) {
				clearTimeout(filterDepsDebounceRef.current);
			}
			filterDepsDebounceRef.current = setTimeout(() => {
				fetchFilterDependencies(activeFilters);
			}, FILTER_DEPS_DEBOUNCE_MS);
		},
		[fetchFilterDependencies]
	);

	useEffect(() => {
		return () => {
			if (filterDepsDebounceRef.current) clearTimeout(filterDepsDebounceRef.current);
			if (filterDepsAbortRef.current) filterDepsAbortRef.current.abort();
		};
	}, []);

	// Server-side typeahead for the Key filter: fetch matches as the user types (debounced)
	// instead of shipping the full distinct-key list with /filter-options.
	const keyOptionsAbortRef = useRef(null);
	const keyOptionsDebounceRef = useRef(null);

	const fetchKeyOptions = useCallback(async (query) => {
		if (keyOptionsAbortRef.current) keyOptionsAbortRef.current.abort();
		const abortController = new AbortController();
		keyOptionsAbortRef.current = abortController;
		try {
			const keys = await fetchPictorialKeyOptions(query, abortController.signal);
			setMasterFilterOptions((prev) => ({ ...prev, keys }));
			setFilterOptions((prev) => ({ ...prev, keys }));
		} catch (err) {
			if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
			console.error("Error fetching key options:", err);
		}
	}, []);

	// Fetch when the Key dropdown opens (prefetch top matches) and as the search term changes.
	useEffect(() => {
		if (!openDropdowns.has("key")) return undefined;
		if (keyOptionsDebounceRef.current) clearTimeout(keyOptionsDebounceRef.current);
		keyOptionsDebounceRef.current = setTimeout(() => {
			fetchKeyOptions(searchTerms.key || "");
		}, 250);
		return () => {
			if (keyOptionsDebounceRef.current) clearTimeout(keyOptionsDebounceRef.current);
		};
	}, [openDropdowns, searchTerms.key, fetchKeyOptions]);

	useEffect(() => {
		return () => {
			if (keyOptionsAbortRef.current) keyOptionsAbortRef.current.abort();
		};
	}, []);

	// Fetch company totals; respects channel, store, city, state, region, tier so header totals match grid
	const fetchCompanyTotals = useCallback(async (startDate, endDate, filtersObj = {}) => {
		try {
			const data = await fetchPictorialCompanyTotals(startDate, endDate, filtersObj);
			if (data) {
				setCompanyTotals(data);
			}
		} catch (err) {
			console.error("Error fetching company totals:", err);
			setCompanyTotals({
				totalBuy: 0,
				totalSold: 0,
				totalSoldOverall: 0,
				balance: 0,
				periodStPct: 0,
				totalStPct: 0,
				gpValue: 0,
				mrpBuyValue: 0,
				mrpSoldValue: 0,
				rareRabbitSoldQty: 0,
				rareRabbitNetSalesValue: 0,
				rareRabbitGpValue: 0,
				innervOnlyBills: 0,
				innervWithOtherBills: 0,
			});
		}
	}, []);

	const fetchProducts = useCallback(
		async (startDate, endDate) => {
			// Increment request ID at the function scope
			const thisRequestId = ++currentRequestIdRef.current;
			setDataLoadPhase("fetching");

			// Start progress simulation for long queries
			setQueryProgress(10);
			const progressInterval = setInterval(() => {
				setQueryProgress((prev) => Math.min(prev + 10, 90));
			}, 500);

			try {
				// Don't set loading here - it's already set by the calling function
				setError("");

				const data = await fetchPictorialProducts(startDate, endDate, filtersRef.current);

				// Check if this is still the latest request
				if (thisRequestId !== currentRequestIdRef.current) {
					return;
				}

				if (!data || !data.products) {
					throw new Error("Invalid response format from server");
				}

				setMetricsChannel(data.metrics_channel ?? null);

				const flattenedProducts = data.products.flat();

				if (flattenedProducts.length === 0) {
					setDataLoadPhase(null);
					setError("No data found for the selected filters. Please try changing the date range or filters.");
					setProducts([]);
					setAllProducts([]);
					setPagination((prev) => ({
						...prev,
						total_pages: 0,
						total: 0,
					}));
					return;
				}

				setDataLoadPhase("rendering");
				// Let the browser paint the "rendering" overlay before heavy JS (sort, totals, slice)
				await new Promise((resolve) => requestAnimationFrame(() => resolve()));

				if (thisRequestId !== currentRequestIdRef.current) {
					return;
				}

				// Process products only if this is still the latest request
				if (thisRequestId === currentRequestIdRef.current) {
					// The backend now returns products in the correct format
					const transformedProducts = flattenedProducts.map((product) => ({
						...product,
						// Ensure all required fields exist with defaults
						troco_ls: product.troco_ls || {
							t_buy: 0,
							t_inw: 0,
							t_sale: 0,
							st: 0,
							mrp: "0",
						},
						lcogs: product.lcogs || {
							gp_percentage: "0.00%",
							gp_value: "0.00",
							oln_cont: "0%",
							offln_cont: "0%",
							sales_cont: "0%",
						},
					}));

					console.log("Setting products:", {
						transformedCount: transformedProducts.length,
						perPage: pagination.per_page,
						totalPages: Math.ceil(transformedProducts.length / pagination.per_page),
					});

					// Apply default sorting to the products
					const sortedProducts = sortProducts(transformedProducts, sortConfig.field, sortConfig.direction);

					setRampColorDrilldown(null);
					setAllProducts(sortedProducts);

					const totalPages = Math.ceil(sortedProducts.length / pagination.per_page);
					setPagination((prev) => ({
						...prev,
						total_pages: totalPages,
						total: sortedProducts.length,
						page: 1, // Reset to first page on new data
					}));

					const currentPageProducts = sortedProducts.slice(0, pagination.per_page);
					console.log("Setting current page products:", currentPageProducts.length);

					// Cache the results if a department filter is applied
					if (filtersRef.current.department) {
						const cacheKey = `department_${filtersRef.current.department}`;
						const cacheData = {
							products: currentPageProducts,
							allProducts: sortedProducts,
							timestamp: Date.now(),
						};
						setProductCache((prev) => new Map(prev).set(cacheKey, cacheData));
						console.log("Cached data for department:", filtersRef.current.department);
					}

					if (sortedProducts.length > 0) {
						const totalBuy = sortedProducts.reduce((sum, product) => sum + (product.troco_ls?.t_buy || 0), 0);
						const totalSold = sortedProducts.reduce((sum, product) => sum + (product.troco_ls?.t_sale || 0), 0);
						const totalSoldLifetime = sortedProducts.reduce(
							(sum, product) => sum + (product.troco_ls?.t_sale_total || 0),
							0
						);
						const currentSoh = sortedProducts.reduce(
							(sum, product) => sum + (Number(product.total_wh_soh) || 0) + (Number(product.total_ebo_soh) || 0),
							0
						);
						const periodDays = Math.max(
							1,
							Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000)) + 1
						);
						const monthlySoldRate = totalSold > 0 ? (totalSold / periodDays) * 30 : 0;
						const stockCoverOneMonth = monthlySoldRate > 0 ? currentSoh / monthlySoldRate : 0;

						// Calculate averages and totals for the new metrics
						const totalCOGS = sortedProducts.reduce((sum, product) => sum + parseFloat(product.COGS || 0), 0);
						const totalGPValue = sumGpValueForProducts(sortedProducts);
						const totalGPPercentage = sortedProducts.reduce(
							(sum, product) => sum + parseFloat(product.lcogs?.gp_percentage?.replace("%", "") || 0),
							0
						);

						const avgCOGS = sortedProducts.length > 0 ? totalCOGS / sortedProducts.length : 0;
						const avgGPPercentage =
							sortedProducts.length > 0 ? Math.round((totalGPPercentage / sortedProducts.length) * 100) / 100 : 0;

						// Calculate ASP (Average Selling Price) based on realised value per unit sold
						const totalRealisedValue = sortedProducts.reduce(
							(sum, product) => sum + parseFloat(product.realised_value || 0),
							0
						);
						const netSalesValue = sortedProducts.reduce(
							(sum, product) => sum + parseFloat(product.net_amount || 0),
							0
						);
						const totalSoldQty = sortedProducts.reduce((sum, product) => sum + (product.troco_ls?.t_sale || 0), 0);

						// Calculate ASP as total realised value / total sold quantity
						let avgASP = 0;
						if (totalSoldQty > 0 && totalRealisedValue > 0) {
							avgASP = totalRealisedValue / totalSoldQty;
						}

						// Exact portfolio sums, then rounded avg MRP × qty for displayed MRP Value
						const mrpValueExact = transformedProducts.reduce(
							(sum, product) => sum + parseFloat(product.troco_ls?.mrp || 0) * (product.troco_ls?.t_buy || 0),
							0
						);
						const soldMRPValueExact = transformedProducts.reduce(
							(sum, product) => sum + parseFloat(product.troco_ls?.mrp || 0) * (product.troco_ls?.t_sale || 0),
							0
						);

						const buyCogsValue = transformedProducts.reduce((sum, product) => {
							const cogs = parseFloat(String(product.COGS ?? "").replace(/,/g, "")) || 0;
							return sum + cogs * (product.troco_ls?.t_buy || 0);
						}, 0);
						const soldCogsValue = transformedProducts.reduce((sum, product) => {
							const cogs = parseFloat(String(product.COGS ?? "").replace(/,/g, "")) || 0;
							return sum + cogs * (product.troco_ls?.t_sale || 0);
						}, 0);

						const avgMRPBuy = totalBuy > 0 ? Math.round(mrpValueExact / totalBuy) : 0;
						const mrpValue = avgMRPBuy * totalBuy;
						const avgMRPSold = totalSold > 0 ? Math.round(soldMRPValueExact / totalSold) : 0;
						const soldMRPValue = avgMRPSold * totalSold;

						const buyAvgCOGS = totalBuy > 0 ? buyCogsValue / totalBuy : 0;
						const buyMulti = buyCogsValue > 0 ? mrpValue / buyCogsValue : 0;
						const soldMulti = soldCogsValue > 0 ? soldMRPValue / soldCogsValue : 0;

						const percentageSold = totalBuy > 0 ? (totalSoldLifetime / totalBuy) * 100 : 0;

						// Sales Contribution (Category Level) - Calculate against company total
						const salesContribution = companyTotals.totalSold > 0 ? (totalSold / companyTotals.totalSold) * 100 : 100;

						// Fix: Cap the GP contribution at 100% to prevent unrealistic percentages
						// This happens when filtered data exceeds company totals due to data inconsistencies
						const rawGpContribution = companyTotals.gpValue > 0 ? (totalGPValue / companyTotals.gpValue) * 100 : 0;

						const gpContribution = Math.min(Math.round(rawGpContribution * 10) / 10, 100);

						setTotals({
							totalBuy: totalBuy,
							totalSold: totalSold,
							totalSoldLifetime: totalSoldLifetime,
							subCategory: filters.subCategory || "All",
							avgASP: avgASP,
							avgMRP: avgMRPBuy,
							avgMRPSold: avgMRPSold,
							buyAvgCOGS: buyAvgCOGS,
							avgCOGS: avgCOGS,
							gpValue: totalGPValue,
							gpPercentage: avgGPPercentage,
							mrpValue: mrpValue,
							soldMRPValue: soldMRPValue,
							buyCogsValue: buyCogsValue,
							soldCogsValue: soldCogsValue,
							buyMulti: buyMulti,
							soldMulti: soldMulti,
							percentageSold: percentageSold,
							salesContribution: salesContribution,
							gpContribution: gpContribution,
							currentSoh: currentSoh,
							stockCoverOneMonth: stockCoverOneMonth,
							netSalesValue: netSalesValue,
						});
					}
				}

				if (thisRequestId === currentRequestIdRef.current) {
					setDataLoadPhase(null);
				}
			} catch (err) {
				console.error("Error in fetchProducts:", err);
				if (err.response && err.response.status === 500) {
					setError("No data found for the selected filters. Please try changing the date range or filters.");
				} else {
					setError(`Failed to fetch products: ${err.message}`);
				}
				setProducts([]);
					setAllProducts([]);
					setMetricsChannel(null);
					setRampColorDrilldown(null);
					setPagination((prev) => ({
						...prev,
						total_pages: 0,
						total: 0,
					}));
				if (currentRequestIdRef.current === thisRequestId) {
					setDataLoadPhase(null);
				}
			} finally {
				// Clear progress interval and complete progress
				clearInterval(progressInterval);
				setQueryProgress(100);

				// Only update loading state if this is the latest request
				if (currentRequestIdRef.current === thisRequestId) {
					setLoading(false);
					setDataLoadPhase(null);
					// Reset progress after a short delay
					setTimeout(() => setQueryProgress(0), 500);
				}
			}
		},
		[pagination.per_page, companyTotals, filters.subCategory, sortConfig.field, sortConfig.direction]
	);

	// Master dropdown lists: load once on mount. Backend `sales_filter_snapshot` is not date-filtered in SQL;
	// changing the report period does not require refetching these options. Backend also applies a 5-minute cache.
	useEffect(() => {
		fetchFilterOptions(dateRange.startDate, dateRange.endDate);

		return () => {
			window.filterOptionsCallInProgress = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: avoid refetch on every date change
	}, [fetchFilterOptions]);

	const companyTotalsFilters = useMemo(
		() => ({
			channel: filters.channel,
			store: filters.store,
			city: filters.city,
			state: filters.state,
			region: filters.region,
			tier: filters.tier,
		}),
		[filters.channel, filters.store, filters.city, filters.state, filters.region, filters.tier]
	);

	// Fetch company totals when date range or location filters change (channel, store, city, state, region, tier)
	useEffect(() => {
		if (products.length > 0) {
			fetchCompanyTotals(dateRange.startDate, dateRange.endDate, companyTotalsFilters);
		}
	}, [dateRange.startDate, dateRange.endDate, companyTotalsFilters, products.length, fetchCompanyTotals]);

	// Fetch products when date range or filters change
	useEffect(() => {
		console.log("Products useEffect triggered:", {
			loading,
			isPageChange: pagination.isPageChange,
			isApplyingFilters: isApplyingFiltersRef.current,
			dateRange: dateRange.startDate.toISOString(),
			filtersCount: Object.keys(filters).length,
		});

		// Only fetch if loading is true and it's not a pagination change and not in the middle of applying filters
		if (loading && !pagination.isPageChange && !isApplyingFiltersRef.current) {
			console.log("Calling fetchProducts...");
			// Fetch immediately without delay for fastest results
			fetchProducts(dateRange.startDate, dateRange.endDate);
		}
	}, [dateRange.startDate, dateRange.endDate, filters, loading, pagination.isPageChange, fetchProducts]);

	// Cache cleanup effect - runs every 10 minutes to clean up expired entries
	useEffect(() => {
		const cleanupInterval = setInterval(() => {
			const CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes
			const now = Date.now();

			setProductCache((prev) => {
				const newCache = new Map();
				let cleaned = false;

				for (const [key, value] of prev) {
					if (now - value.timestamp < CACHE_EXPIRY) {
						newCache.set(key, value);
					} else {
						cleaned = true;
					}
				}

				if (cleaned) {
					console.log("Cleaned up expired cache entries");
				}

				return newCache;
			});
		}, 10 * 60 * 1000); // Run every 10 minutes

		return () => clearInterval(cleanupInterval);
	}, []);

	// Set default sort configuration for client-side sorting
	useEffect(() => {
		setSortConfig({
			field: "period_st",
			direction: "desc",
		});
	}, []);

	// When filters are cleared, reset dropdown options to full list (from filter-options)
	useEffect(() => {
		if (!filterOptionsLoadedRef.current) return;
		const hasActiveFilters = Object.entries(filters).some(
			([key, value]) =>
				value &&
				value.trim() !== "" &&
				key !== "start_date" &&
				key !== "end_date" &&
				key !== "sort_field" &&
				key !== "sort_direction"
		);
		if (!hasActiveFilters && Object.keys(masterFilterOptions).length > 0) {
			setFilterOptions(masterFilterOptions);
		}
	}, [filters, masterFilterOptions]);

	// Close dropdowns and tooltips when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			// Close dropdowns
			if (openDropdowns.size > 0) {
				// Check if click is outside any dropdown
				const isClickInsideDropdown = event.target.closest("[data-dropdown]");
				if (!isClickInsideDropdown) {
					setOpenDropdowns(new Set());
				}
			}

			// Popover handles outside clicks via onClose; keep for legacy nodes if any
			if (gpContributionCard.open) {
				const isClickInsideCard = event.target.closest("[data-gp-card]");
				const isClickOnGpContribution = event.target.closest("[data-gp-contribution]");
				const isClickInsidePopover = event.target.closest(".MuiPopover-root");
				if (!isClickInsideCard && !isClickOnGpContribution && !isClickInsidePopover) {
					closeGpContributionCard();
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [openDropdowns, gpContributionCard.open, closeGpContributionCard]);

	// Trigger dependency filtering when a dropdown closes and filters have changed
	useEffect(() => {
		if (openDropdowns.size > 0 && prevOpenDropdownsSize.current === 0) {
			// Dropdown just opened
			filtersWhenDropdownOpened.current = JSON.stringify(filters);
		} else if (openDropdowns.size === 0 && prevOpenDropdownsSize.current > 0) {
			// Dropdown just closed
			const currentFiltersStr = JSON.stringify(filters);
			if (filtersWhenDropdownOpened.current !== currentFiltersStr && filterOptionsLoadedRef.current) {
				const hasActiveFilters = Object.entries(filters).some(
					([key, value]) =>
						value &&
						String(value).trim() !== "" &&
						!["start_date", "end_date", "sort_field", "sort_direction"].includes(key)
				);

				if (!hasActiveFilters) {
					setFilterOptions(masterFilterOptions);
				} else {
					scheduleFilterDependencies(filters);
				}
			}
		}
		prevOpenDropdownsSize.current = openDropdowns.size;
	}, [openDropdowns.size, filters, masterFilterOptions, scheduleFilterDependencies]);

	const handleStartDateChange = (newDate) => {
		setPendingDateRange((prev) => ({
			...prev,
			startDate: newDate,
		}));
	};

	const handleEndDateChange = (newDate) => {
		setPendingDateRange((prev) => ({
			...prev,
			endDate: newDate,
		}));
	};

	const handlePageChange = (event, newPage) => {
		setPagination((prev) => ({ ...prev, page: newPage }));
	};

	// Defer parent/list update so typing stays instant (input updates sync, list update in transition)
	const setSearchTermsDeferred = useCallback((field, value) => {
		startTransition(() => {
			setSearchTerms((prev) => ({ ...prev, [field]: value }));
		});
	}, []);

	// Only one dropdown open at a time for faster click and less DOM work
	const handleToggleFilterDropdown = useCallback((field) => {
		setOpenDropdowns((prev) => (prev.has(field) ? new Set() : new Set([field])));
	}, []);

	const handleFilterDropdownSelectionChange = useCallback((field, newSet) => {
		const mappedKey = filterKeyMap[field] || `${field}s`;
		setSelectedItems((prev) => ({
			...prev,
			[mappedKey]: newSet,
		}));

		// Update filters immediately
		setFilters((prev) => ({
			...prev,
			[field]: newSet.size > 0 ? Array.from(newSet).join(",") : "",
		}));
	}, []);

	const renderFilterDropdown = (field, label) => {
		const mappedKey = filterKeyMap[field] || `${field}s`;
		return (
			<FilterDropdown
				field={field}
				label={label}
				options={masterFilterOptions[mappedKey] || []}
				validOptions={filterOptions[mappedKey] || []}
				selectedSet={selectedItems[mappedKey] || new Set()}
				isOpen={openDropdowns.has(field)}
				searchTerm={searchTerms[field] || ""}
				onToggle={handleToggleFilterDropdown}
				onSearchChangeDebounced={setSearchTermsDeferred}
				onSelectionChange={handleFilterDropdownSelectionChange}
				alwaysShowSearch={field === "key"}
			/>
		);
	};

	/** Sold in period ÷ buy qty (matches product card Period ST). */
	const calculatePeriodST = useCallback(() => {
		if (totals.totalBuy === 0) return "0.00";
		return ((totals.totalSold / totals.totalBuy) * 100).toFixed(2);
	}, [totals.totalBuy, totals.totalSold]);

	/** Lifetime sell-through: total sold ÷ buy. */
	const calculateST = useCallback(() => {
		if (totals.totalBuy === 0) return "0.00";
		const soldForSt = totals.totalSoldLifetime ?? totals.totalSold;
		return ((soldForSt / totals.totalBuy) * 100).toFixed(2);
	}, [totals.totalBuy, totals.totalSold, totals.totalSoldLifetime]);

	/** Flash the orange highlight ring on a card already in the DOM */
	const flashCard = useCallback((pkey) => {
		const id = `pict-card-${String(pkey).replace(/[^a-zA-Z0-9]/g, "-")}`;
		const el = document.getElementById(id);
		if (!el) return;
		el.scrollIntoView({ behavior: "smooth", block: "center" });
		el.style.transition = "outline 0.15s ease, box-shadow 0.15s ease";
		el.style.outline = "3px solid #e65100";
		el.style.borderRadius = "8px";
		el.style.boxShadow = "0 0 0 5px rgba(230,81,0,0.25)";
		setTimeout(() => {
			el.style.outline = "";
			el.style.boxShadow = "";
			el.style.borderRadius = "";
		}, 1800);
	}, []);

	/** Navigate to the page that contains `pkey` then scroll to it */
	const navigateToProduct = useCallback((pkey) => {
		let idx = paginationSourceList.findIndex((p) => p.PKEY === pkey);
		if (idx === -1) {
			// Similar options are non-broken; they disappear from the list when WH/EBO filter is on
			const idxFull = allProductsForView.findIndex((p) => p.PKEY === pkey);
			if (idxFull === -1) return;
			const targetPage = Math.floor(idxFull / pagination.per_page) + 1;
			pendingScrollPkeyRef.current = pkey;
			pendingScrollJumpRef.current = { pkey, page: targetPage };
			setBrokenFilter(null);
			setAvailableFilter(null);
			setFullSizeSetFilter(null);
			setSelectedSizeFilters([]);
			setSelectedSizeRatioFilters(new Set());
			return;
		}
		const targetPage = Math.floor(idx / pagination.per_page) + 1;
		if (targetPage === pagination.page) {
			flashCard(pkey);
		} else {
			pendingScrollPkeyRef.current = pkey;
			setPagination((prev) => ({ ...prev, page: targetPage }));
		}
	}, [paginationSourceList, allProductsForView, pagination.per_page, pagination.page, flashCard]);

	// After a page change renders, fire the pending scroll if the product is now visible
	useEffect(() => {
		const pkey = pendingScrollPkeyRef.current;
		if (!pkey) return;
		if (!products.some((p) => p.PKEY === pkey)) return;
		const t = setTimeout(() => {
			flashCard(pkey);
			pendingScrollPkeyRef.current = null;
		}, 120);
		return () => clearTimeout(t);
	}, [products, flashCard]);

	const handleSort = (field) => {
		setPagination((prev) => ({ ...prev, page: 1, isPageChange: false }));

		// Update sort config
		const newSortConfig = {
			field: field,
			direction: sortConfig.field === field && sortConfig.direction === "desc" ? "asc" : "desc",
		};
		setSortConfig(newSortConfig);

		// Client-side sorting using cached data
		const sortedProducts = sortProducts(allProducts, field, newSortConfig.direction);

		setAllProducts(sortedProducts);
	};

	/** Up = desc (high → low); down = asc (low → high). Inactive shows up (first click sorts desc). */
	const renderSortButton = (field, label) => {
		const isActive = sortConfig.field === field;
		const isHovered = hoveredSort === field;
		const arrow =
			isActive && sortConfig.direction === "desc" ? (
				<KeyboardArrowUpIcon fontSize="small" />
			) : isActive && sortConfig.direction === "asc" ? (
				<KeyboardArrowDownIcon fontSize="small" />
			) : (
				<KeyboardArrowUpIcon fontSize="small" />
			);

		return (
			<Button
				key={field}
				variant={isActive ? "contained" : "outlined"}
				onClick={() => handleSort(field)}
				size="small"
				onMouseEnter={() => setHoveredSort(field)}
				onMouseLeave={() => setHoveredSort(null)}
				sx={{ position: "relative" }}>
				{label}
				<span
					className="sort-arrow"
					style={{
						opacity: isActive || isHovered ? 1 : 0.3,
						transition: "opacity 0.2s ease",
						marginLeft: "4px",
						display: "inline-flex",
						alignItems: "center",
						pointerEvents: "none",
					}}>
					{arrow}
				</span>
			</Button>
		);
	};

	// Add live clock update
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentDateTime(new Date());
		}, 1000);

		return () => clearInterval(timer);
	}, []);

	const handleDownloadPDF = useCallback(
		async (downloadType = "all", tileFieldMask) => {
			if (pdfGenerating) {
				return;
			}

			const captureEl = pdfRef.current;
			if (!captureEl) {
				setError("Report view is not ready for PDF export.");
				return;
			}

			const mergedPdfFieldMask = { ...defaultExportFieldMask(), ...(tileFieldMask || {}) };
			if (!hasTileExportFieldsSelected(mergedPdfFieldMask)) {
				setError("Select at least one product tile field to include in the PDF.");
				return;
			}
			pdfExportFieldMaskRef.current = mergedPdfFieldMask;

			const perPage = pagination.per_page;
			const exportAllList = productsForAllPagesExport;
			const totalPdfPages =
				downloadType === "current" ? 1 : Math.max(1, Math.ceil(exportAllList.length / perPage));

			if (downloadType === "current") {
				if (!products || products.length === 0) {
					setError("No products available to download");
					return;
				}
			} else if (!exportAllList || exportAllList.length === 0) {
				setError("No products available to download");
				return;
			}

			const restorePage = pagination.page;
			let pdfStyleEl = null;
			let popupNotification = null;
			let blocker = null;

			const restoreListing = () => {
				if (downloadType !== "all") return;
				const listAfterExport = activeQuickFilter ? paginationSourceList : allProducts;
				const startIdx = (restorePage - 1) * perPage;
				flushSync(() => {
					setProducts(listAfterExport.slice(startIdx, startIdx + perPage));
					setPagination((prev) => ({ ...prev, page: restorePage }));
				});
			};

			try {
				setPdfGenerating(true);
				setPdfProgress(0);

				const pdf = new jsPDF({
					orientation: "landscape",
					unit: "mm",
					format: "a2",
				});

				blocker = document.createElement("div");
				blocker.style.cssText =
					"position:fixed;inset:0;background:rgba(255,255,255,0.92);z-index:9998;cursor:wait;";
				document.body.appendChild(blocker);

				popupNotification = document.createElement("div");
				popupNotification.style.cssText = `
					position: fixed;
					top: 20px;
					right: 20px;
					width: 300px;
					background: white;
					border-radius: 12px;
					box-shadow: 0 4px 20px rgba(0,0,0,0.15);
					z-index: 10000;
					font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
					border: 1px solid #e0e0e0;
					overflow: hidden;
					animation: slideInRight 0.3s ease-out;
				`;

				pdfStyleEl = document.createElement("style");
				pdfStyleEl.textContent = `
					@keyframes slideInRight {
						from { transform: translateX(100%); opacity: 0; }
						to { transform: translateX(0); opacity: 1; }
					}
				`;
				document.head.appendChild(pdfStyleEl);

				const progressContent = document.createElement("div");
				progressContent.style.cssText = `padding: 20px; text-align: center;`;
				popupNotification.appendChild(progressContent);

				const progressBarContainer = document.createElement("div");
				progressBarContainer.style.cssText = `
					width: 100%;
					height: 4px;
					background: #e0e0e0;
					border-radius: 2px;
					overflow: hidden;
					margin-top: 10px;
				`;
				popupNotification.appendChild(progressBarContainer);

				const progressBar = document.createElement("div");
				progressBar.style.cssText = `
					height: 100%;
					background: #000;
					width: 0%;
					transition: width 0.3s ease;
					border-radius: 2px;
				`;
				progressBarContainer.appendChild(progressBar);

				document.body.appendChild(popupNotification);

				const gridMarginMm = 14;
				const totalGridPages = totalPdfPages;
				const totalSteps = 1 + totalGridPages;
				let stepsDone = 0;

				const updatePdfProgress = (detail) => {
					stepsDone += 1;
					const progress = Math.min(100, Math.round((stepsDone / totalSteps) * 100));
					setPdfProgress(progress);
					progressContent.innerHTML = `
						<h3 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">Generating PDF</h3>
						<p style="margin: 5px 0; color: #666; font-size: 14px;">${detail}</p>
						<p style="margin: 5px 0; color: #000; font-weight: 600; font-size: 14px;">${progress}% complete</p>
					`;
					progressBar.style.width = `${progress}%`;
				};

				const headerArgs = {
					currentDateTime,
					dateRange,
					totals,
					calculateST,
					calculatePeriodST,
					formatNumber,
					pageWidth: pdf.internal.pageSize.getWidth(),
					filters,
					quickFilterLabel: quickFilterExportLabel,
				};

				// Page 1: summary + applied filters only (no product grid)
				updatePdfProgress("Page 1: summary and applied filters");
				const coverContentEndY = addPdfCoverHeader(pdf, headerArgs);
				pdf.setFontSize(9);
				pdf.setTextColor(100, 100, 100);
				const noteY = Math.min(coverContentEndY + 8, pdf.internal.pageSize.getHeight() - 12);
				pdf.text("Product pictorial begins on the next page.", headerArgs.pageWidth / 2, noteY, {
					align: "center",
				});
				pdf.setTextColor(0, 0, 0);

				for (let gridPage = 1; gridPage <= totalGridPages; gridPage++) {
					updatePdfProgress(`Product grid: page ${gridPage} of ${totalGridPages}`);
					pdf.addPage();

					if (downloadType === "all") {
						const startIdx = (gridPage - 1) * perPage;
						const pageSlice = exportAllList.slice(startIdx, startIdx + perPage);
						flushSync(() => {
							setProducts(pageSlice);
							setPagination((prev) => ({ ...prev, page: gridPage }));
						});
					}

					await waitForImagesInElement(pdfRef.current);
					await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
					await new Promise((r) => setTimeout(r, 150));

					const liveRoot = pdfRef.current;
					const canvas = await html2canvas(liveRoot, {
						scale: 2,
						useCORS: true,
						allowTaint: true,
						backgroundColor: "#ffffff",
						logging: false,
						onclone: (clonedDoc) => {
							preparePdfDomClone(liveRoot, clonedDoc, pdfExportFieldMaskRef.current);
						},
					});

					const pageWidth = pdf.internal.pageSize.getWidth();
					const pageHeight = pdf.internal.pageSize.getHeight();
					const availW = pageWidth - 2 * gridMarginMm;
					const availH = pageHeight - 2 * gridMarginMm;
					const scale = Math.min(availW / canvas.width, availH / canvas.height);
					const scaledW = canvas.width * scale;
					const scaledH = canvas.height * scale;
					const x = (pageWidth - scaledW) / 2;
					const y = gridMarginMm + (availH - scaledH) / 2;
					pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, y, scaledW, scaledH);
				}

				restoreListing();

				if (popupNotification?.parentNode) {
					document.body.removeChild(popupNotification);
				}
				if (blocker?.parentNode) {
					document.body.removeChild(blocker);
				}
				if (pdfStyleEl?.parentNode) {
					pdfStyleEl.remove();
				}

				const filterSlug = quickFilterExportLabel
					? `-${quickFilterExportLabel.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase()}`
					: "";
				const fileName =
					downloadType === "current"
						? `sell-through-report-page-${restorePage}${filterSlug}.pdf`
						: `sell-through-report-all-pages${filterSlug}.pdf`;

				pdf.save(fileName);
			} catch (error) {
				console.error("Error in PDF generation:", error);
				setError(`Failed to generate PDF: ${error.message}`);
				if (popupNotification?.parentNode) {
					document.body.removeChild(popupNotification);
				}
				if (blocker?.parentNode) {
					document.body.removeChild(blocker);
				}
				if (pdfStyleEl?.parentNode) {
					pdfStyleEl.remove();
				}
				restoreListing();
			} finally {
				setPdfGenerating(false);
				setPdfProgress(0);
			}
		},
		[
			pdfGenerating,
			pagination.page,
			pagination.per_page,
			products,
			allProducts,
			productsForAllPagesExport,
			paginationSourceList,
			activeQuickFilter,
			quickFilterExportLabel,
			dateRange,
			totals,
			calculateST,
			calculatePeriodST,
			currentDateTime,
			filters,
		]
	);

	// Add a function to handle applying filters
	const applyFilters = () => {
		setOpenDropdowns(new Set());
		setLoading(true);
		setPagination((prev) => ({ ...prev, page: 1, isPageChange: false }));
		setError("");

		try {
			isApplyingFiltersRef.current = true;
			const formattedStartDate = format(pendingDateRange.startDate, "yyyy-MM-dd");
			const formattedEndDate = format(pendingDateRange.endDate, "yyyy-MM-dd");
			const newFilters = {
				start_date: formattedStartDate,
				end_date: formattedEndDate,
			};

			Object.entries(selectedItems).forEach(([field, items]) => {
				const apiKey = field.endsWith("s") ? field.slice(0, -1) : field;
				if (["subCategories", "cities", "fashionability"].includes(field)) {
					const specialKey = field === "subCategories" ? "subCategory" : field === "cities" ? "city" : "fashionability";
					if (items.size > 0) {
						newFilters[specialKey] = Array.from(items).join(",");
					} else {
						delete newFilters[specialKey];
					}
				} else {
					if (items.size > 0) {
						newFilters[apiKey] = Array.from(items).join(",");
					} else {
						delete newFilters[apiKey];
					}
				}
			});
			setFilters(newFilters);
			setDateRange(pendingDateRange);

			// Trigger dependency filtering for the new filters
			if (filterOptionsLoadedRef.current) {
				// Check if any filters are active and rollback accordingly
				const hasActiveFilters = Object.entries(newFilters).some(
					([key, value]) =>
						value &&
						value.trim() !== "" &&
						key !== "start_date" &&
						key !== "end_date" &&
						key !== "sort_field" &&
						key !== "sort_direction"
				);

				if (!hasActiveFilters) {
					setFilterOptions(masterFilterOptions);
				}
			}

			// Products and company totals will be fetched automatically by useEffect when dateRange changes
		} catch (err) {
			console.error("Error applying filters:", err);
			setError("Error applying filters");
		} finally {
			// Reset immediately without delay
			isApplyingFiltersRef.current = false;
		}
	};

	// Add reset function
	const handleResetFilters = () => {
		// Reset search terms
		setSearchTerms({
			key: "",
			brand: "",
			subBrand: "",
			department: "",
			subCategory: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			nod: "",
			channel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
		});
		setSizeRatioSearchTerm("");
		setSelectedSizeRatioFilters(new Set());

		// Reset pending filters
		setPendingFilters({
			key: "",
			department: "",
			subCategory: "",
			brand: "",
			subBrand: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			nod: "",
			channel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
		});

		// Reset selected items
		setSelectedItems({
			keys: new Set(),
			brands: new Set(),
			subBrands: new Set(),
			departments: new Set(),
			subCategories: new Set(),
			styles: new Set(),
			colors: new Set(),
			sleeves: new Set(),
			divisions: new Set(),
			seasons: new Set(),
			fashionability: new Set(),
			occasions: new Set(),
			palettes: new Set(),
			fitTypes: new Set(),
			fabricBlends: new Set(),
			nods: new Set(),
			channels: new Set(),
			stores: new Set(),
			cities: new Set(),
			states: new Set(),
			regions: new Set(),
			tiers: new Set(),
		});

		// Reset filters but keep the date range
		const { start_date, end_date } = filters;
		setFilters({
			key: "",
			department: "",
			subCategory: "",
			brand: "",
			subBrand: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			nod: "",
			channel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
			start_date,
			end_date,
		});

		// Reset pagination to first page
		setPagination((prev) => ({ ...prev, page: 1 }));

		// Reset filter options to master options (all available options)
		setFilterOptions(masterFilterOptions);

		// Clear products and error
		setProducts([]);
		setAllProducts([]);
		setRampColorDrilldown(null);
		setError("");
	};

	const loadingOverlayMessage =
		dataLoadPhase === "fetching"
			? "Fetching data..."
			: dataLoadPhase === "rendering"
				? "Rendering products..."
				: "Loading...";

	const handleDownloadCSV = useCallback(
		async (downloadType = "all", fieldMask) => {
			try {
				setLoading(true);

				const mergedMask = { ...defaultExportFieldMask(), ...(fieldMask || {}) };
				if (!hasCsvExportContentSelected(mergedMask)) {
					throw new Error("Select at least one product field to include in the CSV.");
				}

				const productsToProcess =
					downloadType === "current" ? products : productsForAllPagesExport;
				const includeBarcodeFacilitySoh = Boolean(mergedMask.csv_soh_split);
				const selectedTileIds = TILE_EXPORT_FIELD_IDS.filter((id) => mergedMask[id]);
				const onlyOptionWithSohSplit =
					includeBarcodeFacilitySoh &&
					selectedTileIds.length === 1 &&
					selectedTileIds[0] === "option";
				const includeProductRows =
					hasTileExportFieldsSelected(mergedMask) && !onlyOptionWithSohSplit;
				const filterSlug = quickFilterExportLabel
					? `-${quickFilterExportLabel.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase()}`
					: "";
				const fileName =
					downloadType === "current"
						? `sell-through-report-page-${pagination.page}${filterSlug}.csv`
						: `sell-through-report-all-pages${filterSlug}.csv`;

				if (!productsToProcess || productsToProcess.length === 0) {
					throw new Error("No data available to download");
				}

				const csvRows = [];
				if (mergedMask.summary_header) {
					csvRows.push(
						[
							escapeCsvCell("Report"),
							escapeCsvCell("Sell Through Pictorial"),
						].join(","),
						...(quickFilterExportLabel
							? [
									[
										escapeCsvCell("Stock quick filter"),
										escapeCsvCell(quickFilterExportLabel),
									].join(","),
								]
							: []),
						[
							escapeCsvCell("Period"),
							escapeCsvCell(
								`${format(dateRange.startDate, "yyyy-MM-dd")} to ${format(dateRange.endDate, "yyyy-MM-dd")}`
							),
						].join(","),
						[
							escapeCsvCell("Total Buy Pcs"),
							escapeCsvCell(totals.totalBuy),
						].join(","),
						[
							escapeCsvCell("Total Sold Pcs (period)"),
							escapeCsvCell(totals.totalSold),
						].join(","),
						[
							escapeCsvCell("Sold Period ST%"),
							escapeCsvCell(`${calculatePeriodST()}%`),
						].join(","),
						[
							escapeCsvCell("Total ST%"),
							escapeCsvCell(`${calculateST()}%`),
						].join(","),
						[
							escapeCsvCell("Buy MRP Value"),
							escapeCsvCell(totals.mrpValue),
						].join(","),
						[
							escapeCsvCell("Sold MRP Value"),
							escapeCsvCell(totals.soldMRPValue),
						].join(","),
						""
					);
				}

				const selectedCols = TILE_EXPORT_FIELD_IDS.filter((id) => mergedMask[id] && EXPORT_CSV_FIELD_MAP[id]).map(
					(id) => ({ id, ...EXPORT_CSV_FIELD_MAP[id] })
				);

				// Tooltip breakdown groups (CSV only — never part of the PDF tiles)
				PICTORIAL_CSV_TOOLTIP_FIELDS.forEach(({ id }) => {
					if (id === "csv_soh_split") return;
					if (mergedMask[id] && EXPORT_CSV_GROUP_FIELD_MAP[id]) {
						selectedCols.push(...EXPORT_CSV_GROUP_FIELD_MAP[id].map((col) => ({ id, ...col })));
					}
				});

				if (includeProductRows) {
					csvRows.push(selectedCols.map((c) => escapeCsvCell(c.header)).join(","));
					productsToProcess.forEach((product) => {
						csvRows.push(
							selectedCols.map((c) => escapeCsvCell(c.getValue(product))).join(",")
						);
					});
				}

				if (includeBarcodeFacilitySoh) {
					const pkeys = [
						...new Set(
							productsToProcess.flatMap((p) => [getProductExportPkey(p), p.PKEY].filter(Boolean))
						),
					];
					const facilityRows = await fetchPictorialBarcodeFacilitySoh(pkeys, {
						store: filters.store,
					});

					if (includeProductRows) {
						csvRows.push("");
					}
					csvRows.push(BARCODE_FACILITY_SOH_CSV_HEADERS.map((h) => escapeCsvCell(h)).join(","));
					facilityRows.forEach((row) => {
						csvRows.push(
							barcodeFacilitySohRowToCsvCells(row)
								.map((cell) => escapeCsvCell(cell))
								.join(",")
						);
					});
				}

				const csvContent = csvRows.join("\n");
				const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
				const link = document.createElement("a");
				const url = URL.createObjectURL(blob);
				link.setAttribute("href", url);
				link.setAttribute("download", fileName);
				link.style.visibility = "hidden";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} catch (error) {
				console.error("Error in CSV generation:", error);
				setError(`Failed to generate CSV: ${error.message}`);
			} finally {
				setLoading(false);
			}
		},
		[products, productsForAllPagesExport, quickFilterExportLabel, pagination.page, dateRange, totals, calculateST, calculatePeriodST, filters.store]
	);

	// Download dropdown handlers
	const handleCurrentPageDownloadClick = (event) => {
		setCurrentPageDownloadAnchor(event.currentTarget);
		setCurrentPageDownloadOpen(!currentPageDownloadOpen);
	};

	const handleAllPagesDownloadClick = (event) => {
		setAllPagesDownloadAnchor(event.currentTarget);
		setAllPagesDownloadOpen(!allPagesDownloadOpen);
	};

	const handleDownloadMenuClose = () => {
		setCurrentPageDownloadAnchor(null);
		setAllPagesDownloadAnchor(null);
		setCurrentPageDownloadOpen(false);
		setAllPagesDownloadOpen(false);
	};

	const openExportFieldPicker = (scope, format = "pdf") => {
		setPdfFieldPickerScope(scope);
		setExportPickerFormat(format);
		setPdfFieldPickerOpen(true);
		handleDownloadMenuClose();
	};

	const handlePdfFieldToggle = (id) => {
		setPdfFieldSelection((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const handlePdfFieldSelectAll = () => {
		setPdfFieldSelection(defaultPdfTileFieldMask());
	};

	const handlePdfFieldClearAll = () => {
		setPdfFieldSelection(
			Object.fromEntries(
				[...PICTORIAL_EXPORT_FIELDS, ...PICTORIAL_CSV_TOOLTIP_FIELDS].map(({ id }) => [id, false])
			)
		);
	};

	const handlePdfFieldPickerCancel = () => {
		setPdfFieldPickerOpen(false);
	};

	const handlePdfFieldPickerExport = () => {
		if (!hasTileExportFieldsSelected(pdfFieldSelection)) {
			setError("Select at least one product field to include in the export.");
			return;
		}
		setError("");
		setPdfFieldPickerOpen(false);
		const mask = { ...pdfFieldSelection };
		if (exportPickerFormat === "csv") {
			handleDownloadCSV(pdfFieldPickerScope, mask);
		} else {
			handleDownloadPDF(pdfFieldPickerScope, mask);
		}
	};

	// Navigation functions
	const openUploadModal = () => setUploadModalOpen(true);
	const closeUploadModal = () => setUploadModalOpen(false);

	// Function to handle applying filters from upload page
	const handleApplyFiltersFromUpload = (filters, dateRange) => {
		try {
			// Set the date range
			setDateRange(dateRange);
			setPendingDateRange(dateRange);

			// Convert filter values to selectedItems format
			const newSelectedItems = { ...selectedItems };
			const newFilters = {
				start_date: format(dateRange.startDate, "yyyy-MM-dd"),
				end_date: format(dateRange.endDate, "yyyy-MM-dd"),
			};

			// Map filter values to selectedItems and filters
			let uploadedKeys = [];
			Object.entries(filters).forEach(([key, value]) => {
				if (key === "start_date" || key === "end_date") return; // Skip date fields

				const values = value
					.split(",")
					.map((v) => v.trim())
					.filter((v) => v);
				if (values.length > 0) {
					// Map filter key to selectedItems key
					let selectedKey;
					switch (key) {
						case "key":
							selectedKey = "keys";
							uploadedKeys = values;
							break;
						case "subCategory":
							selectedKey = "subCategories";
							break;
						case "city":
							selectedKey = "cities";
							break;
						case "palette":
							selectedKey = "palettes";
							break;
						case "fashionability":
							selectedKey = "fashionability";
							break;
						default:
							selectedKey = `${key}s`;
					}

					newSelectedItems[selectedKey] = new Set(values);

					// Also update the filters state immediately
					const apiKey = key;
					newFilters[apiKey] = values.join(",");
				}
			});

			// Set both selectedItems and filters
			setSelectedItems(newSelectedItems);
			setFilters(newFilters);

			// Seed Key dropdown options from CSV upload (typeahead list is otherwise empty until searched).
			if (uploadedKeys.length > 0) {
				setMasterFilterOptions((prev) => ({ ...prev, keys: uploadedKeys }));
				setFilterOptions((prev) => ({ ...prev, keys: uploadedKeys }));
			}

			// Don't auto-apply filters - let user make manual selections first
			// User will need to click the search button to apply all filters
		} catch (error) {
			console.error("Error applying filters from upload:", error);
		}
	};

	// Warn on page/tab close during PDF generation
	useEffect(() => {
		if (pdfGenerating) {
			window.onbeforeunload = (e) => {
				e.preventDefault();
				e.returnValue = "A PDF is still being generated. Are you sure you want to leave this page?";
				return e.returnValue;
			};
		} else {
			window.onbeforeunload = null;
		}
	}, [pdfGenerating]);

	const isInnervSubBrandSelected = useMemo(
		() => {
			const selectedSubBrands = String(filters.subBrand || "")
				.split(",")
				.map((value) => value.trim().toLowerCase())
				.filter(Boolean);
			return selectedSubBrands.length === 1 && selectedSubBrands[0] === "innerv";
		},
		[filters.subBrand]
	);
	const buildInnervContributionRows = useCallback((innervValue, rareRabbitTotal) => {
		const total = Number(rareRabbitTotal) || 0;
		const innerv = Math.max(0, Number(innervValue) || 0);
		const innervPct = total > 0 ? Math.min((innerv / total) * 100, 100) : 0;
		const restPct = Math.max(0, 100 - innervPct);
		return [
			{ label: "Innerv", value: innerv, percentage: innervPct },
			{ label: "Rest RR", value: Math.max(0, total - innerv), percentage: restPct },
		];
	}, []);
	const innervGpContributionRows = useMemo(
		() => buildInnervContributionRows(totals.gpValue, companyTotals.rareRabbitGpValue),
		[buildInnervContributionRows, totals.gpValue, companyTotals.rareRabbitGpValue]
	);
	const innervSoldQtyContributionRows = useMemo(
		() => buildInnervContributionRows(totals.totalSold, companyTotals.rareRabbitSoldQty),
		[buildInnervContributionRows, totals.totalSold, companyTotals.rareRabbitSoldQty]
	);
	const innervBillMixRows = useMemo(() => {
		const innervOnly = Number(companyTotals.innervOnlyBills) || 0;
		const withOthers = Number(companyTotals.innervWithOtherBills) || 0;
		const totalBills = innervOnly + withOthers;
		return [
			{
				label: "Innerv Only",
				value: innervOnly,
				percentage: totalBills > 0 ? (innervOnly / totalBills) * 100 : 0,
			},
			{
				label: "Innerv + Others",
				value: withOthers,
				percentage: totalBills > 0 ? (withOthers / totalBills) * 100 : 0,
			},
		];
	}, [companyTotals.innervOnlyBills, companyTotals.innervWithOtherBills]);
	const showInnervContributionBars = isInnervSubBrandSelected && allProducts.length > 0;
	const renderInnervContributionBar = (title, rows, valueFormatter) => {
		const total = rows.reduce((sum, row) => sum + row.value, 0);
		return (
			<Box sx={{ mt: 1 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
					<Typography
						variant="caption"
						sx={{
							color: "#616161",
							fontWeight: 700,
							fontSize: "0.58rem",
							letterSpacing: "0.04em",
							textTransform: "uppercase",
						}}>
						{title}
					</Typography>
					<Typography variant="caption" sx={{ color: "#616161", fontWeight: 600, fontSize: "0.58rem" }}>
						{valueFormatter(total)}
					</Typography>
				</Box>
				<Box
					sx={{
						width: "100%",
						height: 18,
						display: "flex",
						overflow: "hidden",
						borderRadius: "999px",
						bgcolor: "#f5f5f5",
						boxShadow: "inset 0 2px 4px rgba(0,0,0,0.12)",
					}}>
					{rows.map((row, index) => (
						<Tooltip
							key={row.label}
							title={`${row.label}: ${row.percentage.toFixed(1)}% · ${valueFormatter(row.value)}`}
							arrow>
							<Box
								sx={{
									flex: `0 1 ${row.percentage}%`,
									minWidth: row.percentage > 0 ? 2 : 0,
									bgcolor: index === 0 ? "#424242" : "#d6d6d6",
									borderRight: index < rows.length - 1 ? "1px solid #fff" : "none",
								}}
							/>
						</Tooltip>
					))}
				</Box>
				<Box sx={{ display: "flex", mt: 0.45 }}>
					{rows.map((row) => (
						<Box key={row.label} sx={{ flex: `0 1 ${row.percentage}%`, minWidth: 0, px: 0.125 }}>
							<Typography
								variant="caption"
								title={`${row.label}: ${row.percentage.toFixed(1)}%`}
								sx={{
									display: "block",
									color: "#424242",
									fontSize: "0.52rem",
									fontWeight: 700,
									textAlign: "center",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{row.percentage.toFixed(1)}%
							</Typography>
							<Typography
								variant="caption"
								title={row.label}
								sx={{
									display: "block",
									color: "#616161",
									fontSize: "0.48rem",
									fontWeight: 600,
									textAlign: "center",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{row.label}
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
		);
	};

	const discountPctAvg = (() => {
		const values = allProducts.map((p) => parseFloat(p.discount_percentage || 0)).filter((v) => !isNaN(v) && v >= 0);
		if (values.length === 0) return 0;
		return values.reduce((a, b) => a + b, 0) / values.length;
	})();

	const channelSoldSplit = useMemo(() => {
		const rows = PICTORIAL_CHANNEL_SOLD_DEF.map(({ label, periodKey, overallKey }) => ({
			label,
			period: allProducts.reduce((sum, product) => sum + (Number(product[periodKey]) || 0), 0),
			overall: allProducts.reduce((sum, product) => sum + (Number(product[overallKey]) || 0), 0),
		})).filter((row) => row.period > 0 || row.overall > 0);

		const periodTotal = rows.reduce((sum, row) => sum + row.period, 0);
		const overallTotal = rows.reduce((sum, row) => sum + row.overall, 0);

		return {
			rows: rows.map((row) => ({
				...row,
				periodPct: channelSoldContributionPct(row.period, periodTotal),
				overallPct: channelSoldContributionPct(row.overall, overallTotal),
			})),
			periodTotal,
			overallTotal,
		};
	}, [allProducts]);

	/** GP Cont/Cat Value in header — same scope/rounding as the contribution breakdown. */
	const displayedGpContCatValue = useMemo(() => {
		const sumCategoryGp = (categories) => categories.reduce((sum, cat) => sum + cat.gpValue, 0);

		if (gpContributionCard.open) {
			if (gpContributionCard.viewMode === "categories" && gpContributionCard.categoryContributions.length > 0) {
				return sumCategoryGp(gpContributionCard.categoryContributions);
			}
			if (gpContributionCard.viewMode === "brands" && gpContributionCard.brandContributions.length > 0) {
				return gpContributionCard.brandContributions.reduce((sum, brand) => sum + brand.gpValue, 0);
			}
			if (gpContributionCard.viewMode === "colors") {
				const rows =
					gpContributionCard.colorContributions.length > 0
						? gpContributionCard.colorContributions
						: gpContributionCard.colorContributionsTinw;
				if (rows.length > 0) {
					return rows.reduce((sum, row) => sum + (row.gpValue || 0), 0);
				}
			}
		}

		const selectedBrands = selectedItems.brands;
		if (selectedBrands?.size === 1) {
			const brand = Array.from(selectedBrands)[0];
			return sumGpValueForProducts(
				allProducts.filter((p) => (p.brand || "Unknown").toLowerCase() === brand.toLowerCase())
			);
		}

		return totals.gpValue;
	}, [gpContributionCard, totals.gpValue, selectedItems.brands, allProducts]);

	return (
		<ThemeProvider theme={theme}>
			<Container maxWidth={false} sx={{ pt: 1, px: { xs: 2, sm: 3, md: 4, lg: 6, xl: 8 } }}>
				{filterOptionsLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "80vh",
							flexDirection: "column",
							gap: 2,
						}}>
						<CircularProgress
							variant={queryProgress > 0 ? "determinate" : "indeterminate"}
							value={queryProgress}
							size={60}
							sx={{ color: "#000" }}
						/>
						<Typography variant="h6" sx={{ color: "#000" }}>
							{queryProgress > 0 ? `Loading data... ${queryProgress}%` : "Loading data..."}
						</Typography>
					</Box>
				) : (
					<Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4, backgroundColor: "#fff" }}>
						{/* Sticky Header Action Bar */}
						<Box
							sx={{
								position: "sticky",
								top: 0,
								zIndex: 10,
								backgroundColor: "rgba(255, 255, 255, 0.9)",
								backdropFilter: "blur(8px)",
								pb: 2,
								pt: 1,
								borderBottom: "1px solid #f0f0f0",
								mb: 3,
								display: "flex",
								flexDirection: { xs: "column", xl: "row" },
								alignItems: { xs: "stretch", xl: "center" },
								justifyContent: "space-between",
								gap: 2,
								margin: "-24px -32px 32px -32px",
								padding: "24px 32px 16px 32px",
							}}>
							<Box sx={{ display: "flex", flexDirection: "column" }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<IconButton
										onClick={() => setFiltersExpanded(!filtersExpanded)}
										size="small"
										sx={{ mr: 0.5, ml: -1, color: "#000" }}>
										{filtersExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
									</IconButton>
									<Typography variant="h5" sx={{ color: "#000", fontWeight: 600, m: 0 }}>
										SELL THROUGH PICTORIAL
									</Typography>
								</Box>
								<Typography variant="caption" sx={{ color: "#666", mt: 0.5, fontWeight: 500 }}>
									{format(currentDateTime, "dd MMM yyyy HH:mm")} • {format(dateRange.startDate, "dd MMM yyyy")} - {format(dateRange.endDate, "dd MMM yyyy")} • {totals.subCategory === "All" ? "All Sub Categories" : totals.subCategory}
								</Typography>
								{/* Active Filter Chips */}
								{hasActiveFilters() && (
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
										{Object.entries(filters).map(([k, v]) => {
											if (!v || ["start_date", "end_date"].includes(k)) return null;
											return (
												<Chip
													key={k}
													label={`${k.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}: ${v}`}
													size="small"
													onDelete={() => {
														const newFilters = { ...filters, [k]: "" };
														setFilters(newFilters);
														setPendingFilters(newFilters);
														const mappedKey = filterKeyMap[k] || `${k}s`;
														setSelectedItems((prev) => ({ ...prev, [mappedKey]: new Set() }));
														if (filterOptionsLoadedRef.current) {
															const hasRemainingFilters = Object.entries(newFilters).some(
																([key, value]) =>
																	value &&
																	String(value).trim() !== "" &&
																	!["start_date", "end_date", "sort_field", "sort_direction"].includes(key)
															);
															if (!hasRemainingFilters) {
																setFilterOptions(masterFilterOptions);
															} else {
																scheduleFilterDependencies(newFilters);
															}
														}
													}}
													sx={{ fontSize: "0.65rem", height: 20, backgroundColor: "rgba(0,0,0,0.04)" }}
												/>
											);
										})}
									</Box>
								)}
							</Box>

							{/* Global Actions */}
							<Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: "wrap", gap: 1 }}>
								<Button
									variant="outlined"
									onClick={openUploadModal}
									size="small"
									startIcon={<UploadFileIcon />}
									sx={{
										borderColor: "#28a745",
										color: "#28a745",
										"&:hover": {
											borderColor: "#218838",
											backgroundColor: "rgba(40, 167, 69, 0.04)",
										},
									}}>
									CSV Upload
								</Button>
								<Tooltip
									title={<PictorialStPdfExportTooltipTitle />}
									arrow
									placement="left"
									slotProps={{
										tooltip: {
											sx: {
												maxWidth: 420,
												bgcolor: "grey.900",
												"& .MuiTypography-root": { color: "#fff" },
											},
										},
									}}>
									<IconButton
										size="small"
										aria-label="What metrics and attributes are included in PDF export"
										sx={{
											color: "#666",
											border: "1px solid #e0e0e0",
											borderRadius: 1,
											"&:hover": { bgcolor: "action.hover" },
										}}>
										<HelpOutlineIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Button
									variant="outlined"
									onClick={handleCurrentPageDownloadClick}
									disabled={loading}
									size="small"
									endIcon={currentPageDownloadOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}>
									Download Current Page
								</Button>
								<Menu
									anchorEl={currentPageDownloadAnchor}
									open={Boolean(currentPageDownloadAnchor)}
									onClose={handleDownloadMenuClose}
									anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
									transformOrigin={{ vertical: "top", horizontal: "left" }}>
									<MenuItem onClick={() => openExportFieldPicker("current", "pdf")} sx={{ alignItems: "flex-start", maxWidth: 340, py: 1.5 }}>
										<ListItemText
											primary="As PDF"
											secondary={pdfExportMenuSecondary}
											secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal", display: "block", mt: 0.5, lineHeight: 1.4 } }}
										/>
									</MenuItem>
									<MenuItem onClick={() => openExportFieldPicker("current", "csv")} sx={{ alignItems: "flex-start", maxWidth: 340, py: 1.5 }}>
										<ListItemText
											primary="As CSV"
											secondary="Choose fields (same picker as PDF)."
											secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal", display: "block", mt: 0.5, lineHeight: 1.4 } }}
										/>
									</MenuItem>
								</Menu>
								<Button
									variant="contained"
									onClick={handleAllPagesDownloadClick}
									disabled={loading}
									size="small"
									endIcon={allPagesDownloadOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}>
									Download All Pages
								</Button>
								<Menu
									anchorEl={allPagesDownloadAnchor}
									open={Boolean(allPagesDownloadAnchor)}
									onClose={handleDownloadMenuClose}
									anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
									transformOrigin={{ vertical: "top", horizontal: "left" }}>
									<MenuItem onClick={() => openExportFieldPicker("all", "pdf")} sx={{ alignItems: "flex-start", maxWidth: 340, py: 1.5 }}>
										<ListItemText
											primary="As PDF"
											secondary={pdfExportMenuSecondary}
											secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal", display: "block", mt: 0.5, lineHeight: 1.4 } }}
										/>
									</MenuItem>
									<MenuItem onClick={() => openExportFieldPicker("all", "csv")} sx={{ alignItems: "flex-start", maxWidth: 340, py: 1.5 }}>
										<ListItemText
											primary="As CSV"
											secondary="Choose fields (same picker as PDF)."
											secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal", display: "block", mt: 0.5, lineHeight: 1.4 } }}
										/>
									</MenuItem>
								</Menu>
							</Stack>
						</Box>

							{/* Summary stats — full width so five cards are not clipped */}
							<Grid container spacing={2} sx={{ mb: 3 }}>
							<Grid item xs={12} sx={{ minWidth: 0 }}>
								<Box sx={PICTORIAL_SUMMARY_STRIP_GRID_SX}>
									{/* Channel-wise sold pcs */}
									<Box sx={PICTORIAL_SUMMARY_BOX_SX}>
										<Typography variant="body1" sx={PICTORIAL_SUMMARY_HEADER_SX}>
											SOLD BY CHANNEL
										</Typography>
										<Box sx={{ ...PICTORIAL_CHANNEL_SOLD_GRID_SX, mb: 0.5, pb: 0.5, borderBottom: "1px solid #eee" }}>
											<Box component="span" />
											<Box component="span" sx={{ ...PICTORIAL_CHANNEL_SOLD_VALUE_SX, fontWeight: "bold" }}>
												Period
												<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>pcs (%)</Box>
											</Box>
											<Box component="span" sx={{ ...PICTORIAL_CHANNEL_SOLD_VALUE_SX, fontWeight: "bold" }}>
												Overall
												<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>pcs (%)</Box>
											</Box>
										</Box>
										{channelSoldSplit.rows.length === 0 ? (
											<Typography variant="body2" sx={{ fontSize: "0.625rem", color: "#666", textAlign: "center" }}>
												No channel sales in view
											</Typography>
										) : (
											channelSoldSplit.rows.map((row) => (
												<Box key={row.label} sx={PICTORIAL_CHANNEL_SOLD_GRID_SX}>
													<Box component="span" sx={{ fontWeight: "bold" }}>
														{row.label}:
													</Box>
													<Box component="span" sx={PICTORIAL_CHANNEL_SOLD_VALUE_SX}>
														{formatNumber(row.period)}
														<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>
															({row.periodPct}%)
														</Box>
													</Box>
													<Box component="span" sx={PICTORIAL_CHANNEL_SOLD_VALUE_SX}>
														{formatNumber(row.overall)}
														<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>
															({row.overallPct}%)
														</Box>
													</Box>
												</Box>
											))
										)}
										{channelSoldSplit.rows.length > 0 && (
											<Box
												sx={{
													...PICTORIAL_CHANNEL_SOLD_GRID_SX,
													mb: 0,
													pt: 0.5,
													borderTop: "1px solid #eee",
												}}>
												<Box component="span" sx={{ fontWeight: "bold" }}>Total:</Box>
												<Box component="span" sx={PICTORIAL_CHANNEL_SOLD_VALUE_SX}>
													{formatNumber(channelSoldSplit.periodTotal)}
													<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>
														({channelSoldSplit.periodTotal > 0 ? "100.0" : "0.0"}%)
													</Box>
												</Box>
												<Box component="span" sx={PICTORIAL_CHANNEL_SOLD_VALUE_SX}>
													{formatNumber(channelSoldSplit.overallTotal)}
													<Box component="span" sx={PICTORIAL_SUMMARY_SUBVALUE_SX}>
														({channelSoldSplit.overallTotal > 0 ? "100.0" : "0.0"}%)
													</Box>
												</Box>
											</Box>
										)}
									</Box>

									{/* Buy-based metrics */}
									<Box sx={PICTORIAL_SUMMARY_BOX_SX}>
										<Typography variant="body1" sx={PICTORIAL_SUMMARY_HEADER_SX}>
											INW BASED
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Total Buy Pcs:
											</Box>
											<Box component="span">{formatNumber(totals.totalBuy)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												MRP Value:
											</Box>
											<Box component="span">{formatNumber(totals.mrpValue)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. MRP:
											</Box>
											<Box component="span">{formatNumber(totals.avgMRP)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												COGS Value:
											</Box>
											<Box component="span">{formatNumber(totals.buyCogsValue || 0)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Multi:
											</Box>
											<Box component="span">{(totals.buyMulti || 0).toFixed(2)}</Box>
										</Typography>
									</Box>

									{/* Sold-based metrics */}
									<Box sx={PICTORIAL_SUMMARY_BOX_SX}>
										<Typography variant="body1" sx={PICTORIAL_SUMMARY_HEADER_SX}>
											SOLD BASED
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Sold Pcs (Period):
											</Box>
											<Box
												component="span"
												title="Total buy pcs − sold in period"
												sx={{ whiteSpace: "normal", lineHeight: 1.35 }}>
												{formatNumber(totals.totalSold)}{" "}
												<Box component="span" sx={PICTORIAL_SUMMARY_BAL_SX}>
													(Bal: {formatNumber((totals.totalBuy || 0) - (totals.totalSold || 0))})
												</Box>
											</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Sold Pcs (Overall):
											</Box>
											<Box
												component="span"
												title="Total buy pcs − sold overall"
												sx={{ whiteSpace: "normal", lineHeight: 1.35 }}>
												{formatNumber(totals.totalSoldLifetime || 0)}{" "}
												<Box component="span" sx={PICTORIAL_SUMMARY_BAL_SX}>
													(Bal:{" "}
													{formatNumber((totals.totalBuy || 0) - (totals.totalSoldLifetime || 0))})
												</Box>
											</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												MRP Value:
											</Box>
											<Box component="span">{formatNumber(totals.soldMRPValue)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Stock Cover (1M):
											</Box>
											<Box
												component="span"
												title="Current SOH divided by 30-day sales run rate from the selected period">
												{(totals.stockCoverOneMonth || 0).toFixed(1)} mo
											</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. MRP:
											</Box>
											<Box component="span">{formatNumber(totals.avgMRPSold)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. ASP:
											</Box>
											<Box component="span">{formatNumber(totals.avgASP)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												COGS Value:
											</Box>
											<Box component="span">{formatNumber(totals.soldCogsValue || 0)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. COGS:
											</Box>
											<Box component="span">{formatAvgRate(totals.buyAvgCOGS)}</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Multi:
											</Box>
											<Box component="span">{(totals.soldMulti || 0).toFixed(2)}</Box>
										</Typography>
									</Box>

									{/* Third Section - Performance metrics */}
									<Box sx={PICTORIAL_SUMMARY_BOX_SX}>
										<Typography variant="body1" sx={PICTORIAL_SUMMARY_HEADER_SX}>
											PERFORMANCE
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Sold Period ST%:
											</Box>
											<Box component="span">{calculatePeriodST()}%</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Total ST%:
											</Box>
											<Box component="span">{calculateST()}%</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Disc%:
											</Box>
											<Box component="span">{discountPctAvg.toFixed(2)}%</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={PICTORIAL_SUMMARY_ROW_SX}>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												Avg. GP%:
											</Box>
											<Box component="span">{formatGPPercentage(totals.gpPercentage)}%</Box>
										</Typography>
										<Typography
											variant="body1"
											sx={{
												...PICTORIAL_SUMMARY_ROW_SX,
												mb: 0,
												cursor: "pointer",
												"&:hover": {
													backgroundColor: "rgba(0, 0, 0, 0.04)",
												},
											}}
											onClick={handleGpContributionClick}
											data-gp-contribution>
											<Box component="span" sx={{ fontWeight: "bold" }}>
												GP Cont/Cat Value:
												{filters.brand &&
													filters.brand.trim() !== "" &&
													selectedItems.brands &&
													selectedItems.brands.size > 1 && (
														<Box
															component="span"
															sx={{
																fontSize: "0.6rem",
																fontWeight: "normal",
																color: "#666",
																ml: 1,
															}}>
															{filters.subCategory && filters.subCategory.trim() !== ""
																? `(100% for ${filters.brand} - ${filters.subCategory})`
																: `(100% for ${filters.brand})`}
														</Box>
													)}
											</Box>
											<Box component="span">{formatGPValue(displayedGpContCatValue)}</Box>
										</Typography>

										<Popover
											open={gpContributionCard.open}
											anchorEl={gpContributionAnchorEl}
											onClose={closeGpContributionCard}
											anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
											transformOrigin={{ vertical: "top", horizontal: "right" }}
											disableRestoreFocus
											slotProps={{
												paper: {
													sx: {
														mt: 0.5,
														width: "min(400px, calc(100vw - 48px))",
														maxHeight: "min(500px, 70vh)",
														bgcolor: "transparent",
														boxShadow: "none",
														overflow: "visible",
													},
												},
											}}>
											<Box
												data-gp-card
												sx={{
													width: "min(400px, calc(100vw - 48px))",
													minHeight: "200px",
													maxHeight: "min(500px, 70vh)",
													display: "flex",
													flexDirection: "column",
												}}>
													<Box
														sx={{
															width: "100%",
															minHeight: "100%",
															backgroundColor: "rgba(0, 0, 0, 0.9)",
															borderRadius: "6px",
															padding: "12px",
															boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
															display: "flex",
															flexDirection: "column",
															transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
															transform: gpContributionCard.transitioning
																? "translateX(100%)"
																: "translateX(0)",
															opacity: gpContributionCard.transitioning ? 0 : 1,
														}}>
														{/* Header */}
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																position: "relative",
																mb: 2,
																pb: 1.5,
																borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
															}}>
															{(gpContributionCard.viewMode === "brands" ||
																gpContributionCard.viewMode === "categories") && (
																<Button
																	variant="text"
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		openColorContributionView(
																			gpContributionCard.viewMode === "categories"
																				? "categories"
																				: "brands",
																			gpContributionCard.selectedBrand
																		);
																	}}
																	sx={{
																		position: "absolute",
																		left: 0,
																		top: "50%",
																		transform: "translateY(-50%)",
																		color: "#81c784",
																		fontSize: "0.65rem",
																		minWidth: "auto",
																		py: 0,
																		px: 0.5,
																		textTransform: "none",
																	}}>
																	By color
																</Button>
															)}
															<Box sx={{ textAlign: "center" }}>
																<Typography
																	variant="h6"
																	sx={{
																		color: "white",
																		fontWeight: 600,
																		mb: 0.25,
																		fontSize: "0.9375rem",
																	}}>
																	{gpContributionCard.viewMode === "colors"
																		? "GP Contribution by Color"
																		: gpContributionCard.viewMode === "categories"
																			? "GP Contribution by Category"
																			: "GP Contribution by Brand"}
																</Typography>
																{(gpContributionCard.viewMode === "categories" ||
																	(gpContributionCard.viewMode === "colors" &&
																		gpContributionCard.colorViewFrom === "categories")) &&
																	gpContributionCard.selectedBrand && (
																		<Typography
																			variant="body2"
																			sx={{
																				color: "rgba(255, 255, 255, 0.7)",
																				fontSize: "0.6875rem",
																			}}>
																			Brand: {gpContributionCard.selectedBrand}
																		</Typography>
																	)}
																{gpContributionCard.viewMode === "colors" &&
																	gpContributionCard.colorViewFrom === "brands" && (
																		<Typography
																			variant="body2"
																			sx={{
																				color: "rgba(255, 255, 255, 0.7)",
																				fontSize: "0.6875rem",
																			}}>
																			All loaded products
																		</Typography>
																	)}
															</Box>
															{(gpContributionCard.viewMode === "categories" ||
																gpContributionCard.viewMode === "colors") && (
																<Button
																	variant="outlined"
																	size="small"
																	sx={{
																		position: "absolute",
																		right: 0,
																		top: "50%",
																		transform: "translateY(-50%)",
																		color: "#FFFFFF",
																		borderColor: "#FFFFFF",
																		minWidth: "auto",
																		width: 32,
																		height: 32,
																		p: 0,
																		borderRadius: "50%",
																		"&:hover": {
																			backgroundColor: "rgba(76, 175, 80, 0.1)",
																			borderColor: "#FFFFFF",
																		},
																	}}
																	onClick={
																		gpContributionCard.viewMode === "colors"
																			? handleBackFromColors
																			: handleBackToBrands
																	}>
																	←
																</Button>
															)}
														</Box>

														{/* Summary Stats */}
														{gpContributionCard.viewMode === "categories" &&
															gpContributionCard.categoryContributions.length > 0 && (
																<Box
																	sx={{
																		display: "flex",
																		gap: 1.5,
																		mb: 2,
																		flexWrap: "wrap",
																	}}>
																	<Chip
																		label={`${gpContributionCard.categoryContributions.length} Categories`}
																		size="small"
																		sx={{
																			backgroundColor: "rgba(76, 175, 80, 0.2)",
																			color: "#FFFFFF",
																			fontWeight: 600,
																		}}
																	/>
																	<Chip
																		label={`Total: ${formatGPValue(
																			gpContributionCard.categoryContributions.reduce(
																				(sum, cat) => sum + cat.gpValue,
																				0
																			)
																		)}`}
																		size="small"
																		sx={{
																			backgroundColor: "rgba(255, 255, 255, 0.1)",
																			color: "white",
																			fontWeight: 600,
																		}}
																	/>
																</Box>
															)}

														{gpContributionCard.viewMode === "colors" &&
															(gpContributionCard.colorContributions.length > 0 ||
																gpContributionCard.colorContributionsTinw.length > 0 ||
																gpContributionCard.colorContributionsSoh.length > 0) && (
																<Box
																	sx={{
																		display: "flex",
																		gap: 1.5,
																		mb: 2,
																		flexWrap: "wrap",
																	}}>
																	{gpContributionCard.colorContributions.length > 0 && (
																		<>
																			<Chip
																				label={`${gpContributionCard.colorContributions.length} Colors (sold)`}
																				size="small"
																				sx={{
																					backgroundColor: "rgba(129, 199, 132, 0.25)",
																					color: "#FFFFFF",
																					fontWeight: 600,
																				}}
																			/>
																			<Chip
																				label={`Sold pcs: ${formatNumber(
																					gpContributionCard.colorContributions.reduce(
																						(sum, row) => sum + (row.soldQty || 0),
																						0
																					)
																				)}`}
																				size="small"
																				sx={{
																					backgroundColor: "rgba(255, 255, 255, 0.1)",
																					color: "white",
																					fontWeight: 600,
																				}}
																			/>
																		</>
																	)}
																	{gpContributionCard.colorContributionsTinw.length > 0 && (
																		<Chip
																			label={`T.INW pcs: ${formatNumber(
																				gpContributionCard.colorContributionsTinw.reduce(
																					(sum, row) => sum + (row.soldQty || 0),
																					0
																				)
																			)}`}
																			size="small"
																			sx={{
																				backgroundColor: "rgba(255, 255, 255, 0.1)",
																				color: "white",
																				fontWeight: 600,
																			}}
																		/>
																	)}
																	{gpContributionCard.colorContributionsSoh.length > 0 && (
																		<Chip
																			label={`SOH pcs: ${formatNumber(
																				gpContributionCard.colorContributionsSoh.reduce(
																					(sum, row) => sum + (row.soldQty || 0),
																					0
																				)
																			)}`}
																			size="small"
																			sx={{
																				backgroundColor: "rgba(255, 255, 255, 0.1)",
																				color: "white",
																				fontWeight: 600,
																			}}
																		/>
																	)}
																</Box>
															)}

														{/* Content */}
														<Box
															sx={{
																flex: "1 1 auto",
																px: 0.5,
																display: "flex",
																flexDirection: "column",
																overflow: "hidden",
															}}>
															<Box
																sx={{
																	flex: "1 1 auto",
																	minHeight: "150px",
																	maxHeight:
																		gpContributionCard.viewMode === "colors"
																			? "none"
																			: "300px",
																	overflowY:
																		gpContributionCard.viewMode === "colors"
																			? "visible"
																			: "auto",
																	overflowX: "hidden",
																	"&::-webkit-scrollbar": {
																		width: "1px",
																	},
																	"&::-webkit-scrollbar-track": {
																		background: "transparent",
																		margin: "0",
																	},
																	"&::-webkit-scrollbar-thumb": {
																		background: "rgba(255, 255, 255, 0.2)",
																		borderRadius: "0.5px",
																		border: "none",
																		"&:hover": {
																			background: "rgba(255, 255, 255, 0.4)",
																		},
																	},
																	"&::-webkit-scrollbar-thumb:hover": {
																		background: "rgba(255, 255, 255, 0.4)",
																	},
																	// Firefox scrollbar styling
																	scrollbarWidth: "thin",
																	scrollbarColor: "rgba(255, 255, 255, 0.2) transparent",
																}}>
																{gpContributionCard.viewMode === "colors" ? (
																	gpContributionCard.colorContributions.length > 0 ||
																	gpContributionCard.colorContributionsTinw.length > 0 ||
																	gpContributionCard.colorContributionsSoh.length > 0 ? (
																		<Box
																			sx={{
																				py: 0.5,
																				textAlign: "left",
																				display: "flex",
																				flexDirection: "column",
																				alignItems: "stretch",
																				gap: 1.5,
																				width: "100%",
																			}}>
																			{gpContributionCard.colorContributionsTinw.length > 0 && (
																				<Box sx={{ width: "100%", minWidth: 0 }}>
																					<ColorContributionRamp
																						rows={gpContributionCard.colorContributionsTinw}
																						title="COLOUR · T.INW %"
																						amountLabel="T.INW"
																						variant="dark"
																						barHeight={30}
																						showSegmentContribution
																						contributionLabel="COGS"
																						onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
																						selectedFilterKey={rampColorSelectedKey}
																					/>
																				</Box>
																			)}
																			{gpContributionCard.colorContributions.length > 0 && (
																				<Box sx={{ width: "100%", minWidth: 0 }}>
																					<ColorContributionRamp
																						rows={gpContributionCard.colorContributions}
																						title="COLOUR · PERIOD SOLD QTY %"
																						amountLabel="sold qty"
																						variant="dark"
																						barHeight={30}
																						showSegmentGp
																						onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
																						selectedFilterKey={rampColorSelectedKey}
																					/>
																				</Box>
																			)}
																			{gpContributionCard.colorContributionsSoh.length > 0 && (
																				<Box sx={{ width: "100%", minWidth: 0 }}>
																					<ColorContributionRamp
																						rows={gpContributionCard.colorContributionsSoh}
																						title="COLOUR · SOH %"
																						amountLabel="SOH"
																						variant="dark"
																						barHeight={30}
																						sohWarehouseSplit
																						onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
																						selectedFilterKey={rampColorSelectedKey}
																					/>
																				</Box>
																			)}
																		</Box>
																	) : (
																		<Box sx={{ textAlign: "center", py: 4 }}>
																			<Typography
																				variant="body1"
																				sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 1 }}>
																				No colour breakdown in this view — need positive T.INW,
																				sold qty, or SOH by colour in the loaded slice.
																			</Typography>
																		</Box>
																	)
																) : gpContributionCard.viewMode === "categories" ? (
																	// Show category contributions for selected brand
																	gpContributionCard.categoryContributions.length > 0 ? (
																		<List
																			sx={{
																				p: 0,
																				flex: "1 1 auto",
																				display: "flex",
																				flexDirection: "column",
																				gap: 0.5,
																			}}>
																			{gpContributionCard.categoryContributions.map(
																				(category, index) => (
																					<ListItem
																						key={category.category}
																						sx={{
																							px: 0,
																							py: 0.5,
																							borderRadius: 0.5,
																							mb: 0.25,
																							cursor: "default",
																						}}>
																						<ListItemText
																							primary={
																								<Box
																									sx={{
																										display: "flex",
																										alignItems: "center",
																										justifyContent:
																											"space-between",
																									}}>
																									<Typography
																										variant="body2"
																										sx={{
																											color: "white",
																											fontWeight: 500,
																											fontSize: "0.6875rem",
																										}}>
																										{category.category}
																									</Typography>
																									<Box
																										sx={{
																											display: "flex",
																											alignItems: "center",
																											gap: 1,
																										}}>
																										<Typography
																											variant="body2"
																											sx={{
																												color: "#FFFFFF",
																												fontWeight: 600,
																												fontSize: "0.6875rem",
																											}}>
																											{formatGPValue(
																												category.gpValue
																											)}
																										</Typography>
																										<Chip
																											label={`${category.percentage.toFixed(
																												1
																											)}%`}
																											size="small"
																											sx={{
																												backgroundColor:
																													"rgba(76, 175, 80, 0.2)",
																												color: "#FFFFFF",
																												fontWeight: 600,
																												fontSize: "0.6rem",
																												height: 20,
																												"& .MuiChip-label": {
																													px: 0.5,
																												},
																											}}
																										/>
																									</Box>
																								</Box>
																							}
																							secondary={
																								<Box sx={{ mt: 0.25 }}>
																									<LinearProgress
																										variant="determinate"
																										value={category.percentage}
																										sx={{
																											height: 3,
																											borderRadius: 1.5,
																											backgroundColor:
																												"rgba(255, 255, 255, 0.1)",
																											"& .MuiLinearProgress-bar":
																												{
																													backgroundColor:
																														"#FFFFFF",
																													borderRadius: 2,
																												},
																										}}
																									/>
																								</Box>
																							}
																						/>
																					</ListItem>
																				)
																			)}
																		</List>
																	) : (
																		<Box sx={{ textAlign: "center", py: 4 }}>
																			<Typography
																				variant="body1"
																				sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 1 }}>
																				No categories found for
																			</Typography>
																			<Typography
																				variant="h6"
																				sx={{ color: "white", fontWeight: 600 }}>
																				{gpContributionCard.selectedBrand}
																			</Typography>
																		</Box>
																	)
																) : (
																	// Show brand contributions
																	<List
																		sx={{
																			p: 0,
																			flex: "1 1 auto",
																			display: "flex",
																			flexDirection: "column",
																			gap: 0.5,
																		}}>
																		{gpContributionCard.brandContributions.map((brand, index) => (
																			<ListItem
																				key={brand.brand}
																				sx={{
																					px: 0,
																					py: 0.5,
																					borderRadius: 0.5,
																					mb: 0.25,
																					"&:hover": {
																						backgroundColor: "rgba(255, 255, 255, 0.1)",
																						cursor: "pointer",
																					},
																					transition: "background-color 0.2s ease",
																				}}
																				onClick={() => handleBrandClick(brand.brand)}>
																				<ListItemText
																					primary={
																						<Box
																							sx={{
																								display: "flex",
																								alignItems: "center",
																								justifyContent: "space-between",
																							}}>
																							<Typography
																								variant="body2"
																								sx={{
																									color: "white",
																									fontWeight: 500,
																									fontSize: "0.6875rem",
																								}}>
																								{brand.brand}
																							</Typography>
																							<Box
																								sx={{
																									display: "flex",
																									alignItems: "center",
																									gap: 1,
																								}}>
																								<Typography
																									variant="body2"
																									sx={{
																										color: "#FFFFFF",
																										fontWeight: 600,
																										fontSize: "0.6875rem",
																									}}>
																									{formatGPValue(brand.gpValue)}
																								</Typography>
																								<Chip
																									label={`${brand.percentage.toFixed(
																										1
																									)}%`}
																									size="small"
																									sx={{
																										backgroundColor:
																											"rgba(76, 175, 80, 0.2)",
																										color: "#FFFFFF",
																										fontWeight: 600,
																										fontSize: "0.6rem",
																										height: 20,
																										"& .MuiChip-label": {
																											px: 0.5,
																										},
																									}}
																								/>
																							</Box>
																						</Box>
																					}
																					secondary={
																						<Box sx={{ mt: 0.25 }}>
																							<LinearProgress
																								variant="determinate"
																								value={brand.percentage}
																								sx={{
																									height: 3,
																									borderRadius: 1.5,
																									backgroundColor:
																										"rgba(255, 255, 255, 0.1)",
																									"& .MuiLinearProgress-bar": {
																										backgroundColor: "#FFFFFF",
																										borderRadius: 2,
																									},
																								}}
																							/>
																						</Box>
																					}
																				/>
																			</ListItem>
																		))}
																	</List>
																)}
															</Box>
														</Box>
													</Box>
												</Box>
										</Popover>
									</Box>
								</Box>
							</Grid>

							{/* Filters Row — horizontal */}
							{filtersExpanded && (
								<Grid item xs={12}>
									<Box sx={{ mb: 2, position: "relative" }}>
										{error && (
											<Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError("")}>
												{error}
											</Alert>
										)}
										<Box
											sx={{
												backgroundColor: "#fafafa",
												borderRadius: "12px",
												padding: { xs: "16px", sm: "20px", md: "24px" },
												border: "1px solid #f0f0f0",
											}}>
											<LocalizationProvider dateAdapter={AdapterDateFns}>
												<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
													<Grid item xs={12} sm={6} md={3}>
														<DatePicker
															label="Start Date"
															value={pendingDateRange.startDate}
															onChange={handleStartDateChange}
															slotProps={{ textField: { size: "small", fullWidth: true } }}
															format="dd-MM-yyyy"
														/>
													</Grid>
													<Grid item xs={12} sm={6} md={3}>
														<DatePicker
															label="End Date"
															value={pendingDateRange.endDate}
															onChange={handleEndDateChange}
															minDate={pendingDateRange.startDate}
															slotProps={{ textField: { size: "small", fullWidth: true } }}
															format="dd-MM-yyyy"
														/>
													</Grid>
													<Grid item xs={12} md={6}>
														<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
															<Button variant="contained" onClick={applyFilters} disabled={loading} size="small">
																Apply
															</Button>
															<Button variant="outlined" onClick={handleResetFilters} size="small" sx={{ minWidth: "40px", p: 0 }}>
																<RefreshIcon fontSize="small" />
															</Button>
															{!loading && products.length > 0 && (
																<Typography variant="caption" sx={{ color: "#666", fontSize: "0.6875rem", fontWeight: 500 }}>
																	{pagination.total} results
																</Typography>
															)}
														</Box>
													</Grid>
												</Grid>
											</LocalizationProvider>
											<Grid container spacing={2}>
												<Grid item xs={6} sm={4} md={1.5}>
													<SizeRatioFilterDropdown
														options={availableSizeRatioOptions}
														selectedSet={selectedSizeRatioFilters}
														isOpen={openDropdowns.has("sizeRatio")}
														searchTerm={sizeRatioSearchTerm}
														onToggle={() => handleToggleFilterDropdown("sizeRatio")}
														onSearchChangeDebounced={(_, value) => setSizeRatioSearchTerm(value)}
														onSelectionChange={setSelectedSizeRatioFilters}
													/>
												</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("brand", "Brand")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("subBrand", "Sub Brand")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("department", "Department")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("subCategory", "Sub Cat.")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("style", "Style")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("color", "Color")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("sleeve", "Sleeve")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("season", "Season")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("division", "Division")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("fashionability", "Fashionability")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("occasion", "Occasion")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("palette", "Palettes")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("fitType", "Fit Type")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("fabricBlend", "Blend")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("nod", "NOD")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("channel", "Channel")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("store", "Store")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("city", "City")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("aging", "Aging")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("st_percentage", "ST%")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("region", "Region")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("tier", "Tier")}</Grid>
												<Grid item xs={6} sm={4} md={1.5}>{renderFilterDropdown("key", "Key")}</Grid>
											</Grid>
										</Box>
										{filterOptionsLoading && (
											<Box
												sx={{
													position: "absolute",
													top: 0,
													left: 0,
													right: 0,
													bottom: 0,
													bgcolor: "rgba(255,255,255,0.6)",
													zIndex: 10,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													borderRadius: "12px",
												}}>
												<CircularProgress size={32} />
											</Box>
										)}
									</Box>
								</Grid>
							)}

							{/* Full-width colour ramps — T.INW first, then sold qty (GP share on sold ramp via showSegmentGp) */}
							{(colorContributionRowsLoaded.length > 0 ||
								colorContributionRowsOverallLoaded.length > 0 ||
								colorContributionRowsTinwLoaded.length > 0 ||
								colorContributionRowsSohLoaded.length > 0 ||
								fashionabilityContributionRowsLoaded.length > 0 ||
								fashionabilityContributionRowsOverallLoaded.length > 0 ||
								showInnervContributionBars) && (
								<Grid item xs={12} sx={{ mt: { xs: 2, md: 1 } }}>
									<Box
										sx={{
											width: "100%",
											display: "flex",
											flexDirection: "column",
											alignItems: "stretch",
											gap: { xs: 2, sm: 2.5, md: 3 },
										}}
										onClick={(e) => e.stopPropagation()}>
										{colorContributionRowsTinwLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<ColorContributionRamp
													rows={colorContributionRowsTinwLoaded}
													title="COLOUR · T.INW %"
													amountLabel="T.INW"
													variant="light"
													showSegmentContribution
													contributionLabel="COGS"
													onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
													selectedFilterKey={rampColorSelectedKey}
												/>
											</Box>
										)}
										{colorContributionRowsLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<ColorContributionRamp
													rows={colorContributionRowsLoaded}
													title="COLOUR · PERIOD SOLD QTY %"
													amountLabel="sold qty"
													variant="light"
													showSegmentGp
													onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
													selectedFilterKey={rampColorSelectedKey}
												/>
											</Box>
										)}
										{colorContributionRowsOverallLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<ColorContributionRamp
													rows={colorContributionRowsOverallLoaded}
													title="COLOUR · OVERALL SOLD QTY %"
													amountLabel="overall sold qty"
													variant="light"
													showSegmentGp
													onSegmentClick={(row) => handleRampSegmentClick(row, "color_overall")}
													selectedFilterKey={rampColorOverallSelectedKey}
												/>
											</Box>
										)}
										{fashionabilityContributionRowsLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<FashionabilityContributionRamp
													rows={fashionabilityContributionRowsLoaded}
													title="FASHIONABILITY · PERIOD SOLD QTY %"
													amountLabel="sold qty"
													onSegmentClick={(row) => handleRampSegmentClick(row, "fashionability")}
													selectedFilterKey={rampFashionabilitySelectedKey}
												/>
											</Box>
										)}
										{fashionabilityContributionRowsOverallLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<FashionabilityContributionRamp
													rows={fashionabilityContributionRowsOverallLoaded}
													title="FASHIONABILITY · OVERALL SOLD QTY %"
													amountLabel="overall sold qty"
													onSegmentClick={(row) =>
														handleRampSegmentClick(row, "fashionability_overall")
													}
													selectedFilterKey={rampFashionabilityOverallSelectedKey}
												/>
											</Box>
										)}
										{showInnervContributionBars && (
											<Box sx={{ width: "100%" }}>
												{renderInnervContributionBar(
													"GP Contribution to Entire Rare Rabbit",
													innervGpContributionRows,
													formatGPValue
												)}
												{renderInnervContributionBar(
													"Sold Qty Contribution to Entire Rare Rabbit",
													innervSoldQtyContributionRows,
													formatNumber
												)}
												{renderInnervContributionBar("Innerv Total Bills", innervBillMixRows, formatNumber)}
											</Box>
										)}
										{colorContributionRowsSohLoaded.length > 0 && (
											<Box sx={{ width: "100%" }}>
												<ColorContributionRamp
													rows={colorContributionRowsSohLoaded}
													title="COLOUR · SOH %"
													amountLabel="SOH"
													variant="light"
													sohWarehouseSplit
													onSegmentClick={(row) => handleRampSegmentClick(row, "color")}
													selectedFilterKey={rampColorSelectedKey}
												/>
											</Box>
										)}
										{rampColorDrilldown && (
											<Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
												<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
													Showing grid:
												</Typography>
												<Chip
													label={
														rampColorDrilldown.dimension === "fashionability"
															? `Fashionability: ${rampColorDrilldown.label}`
															: rampColorDrilldown.dimension === "fashionability_overall"
																? `Fashionability (overall): ${rampColorDrilldown.label}`
																: rampColorDrilldown.dimension === "color_overall"
																	? `Colour (overall): ${rampColorDrilldown.label}`
																	: rampColorDrilldown.label
													}
													onDelete={() => setRampColorDrilldown(null)}
													size="small"
													color="primary"
													variant="outlined"
												/>
											</Box>
										)}
									</Box>
								</Grid>
							)}
						</Grid>

						{/* Enhanced Filters Section */}
						{/* Controls Row - Sort, actions and Download on same line */}
						{filtersExpanded && (
							<Box
								sx={{
									mb: 3,
									mt: 4,
									"& .MuiButton-root": { fontSize: "0.6875rem", py: 0.25, px: 1, minWidth: "auto" },
									"& .MuiButton-startIcon": { mr: 0.25 },
									"& .MuiButton-endIcon": { ml: 0.25 },
									"& .controls-right .MuiButton-root": { fontSize: "0.75rem", py: 0.5, px: 1.5 },
								}}>
								<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="space-between" width="100%">
									<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
											{renderSortButton("period_st", "Period ST")}
											{renderSortButton("st", "Total ST")}
											{renderSortButton("sold_in_period", "Period Sold")}
											{renderSortButton("total_sold", "Total Sold")}
											{renderSortButton("inw_qty", "Inw Qty")}
											{renderSortButton("cogs", "COGS")}
											{renderSortButton("multi_mrp_cogs", "Multi")}
											{renderSortButton("gp_cont", "GP Cont")}
											{renderSortButton("period_ros", "Period Wgt. ROS")}
											{renderSortButton("ros", "Wgt. ROS")}
											{renderSortButton("doh", "DOH")}
											{renderSortButton("aging", "Aging")}
											{renderSortButton("max_recv_aging", "Last Aging")}
											{renderSortButton("wh_soh", "WH SOH")}
											{renderSortButton("ebo_soh", "EBO SOH")}
											<Button
												variant="outlined"
												onClick={handleResetFilters}
												size="small"
												startIcon={<RefreshIcon />}
												sx={{
													borderColor: "#ff4444",
													color: "#ff4444",
													"&:hover": {
														borderColor: "#ff0000",
														backgroundColor: "rgba(255, 0, 0, 0.04)",
													},
												}}>
												Reset
											</Button>
									</Stack>
								</Stack>
							</Box>
						)}


						{/* Loading Indicator */}
						{loading && <LoadingOverlay message={loadingOverlayMessage} />}

						{/* Result Count Display */}
						{!loading && products.length > 0 && (
							<Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography
									variant="body1"
									sx={{
										fontWeight: 500,
										color: "#666",
										fontSize: "0.8125rem",
									}}>
									Showing {products.length} of {pagination.total} products
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: "#999",
										fontSize: "0.6875rem",
									}}>
									Page {pagination.page} of {pagination.total_pages}
								</Typography>
							</Box>
						)}

						{/* No Results Message - Only show when filters are applied */}
						{!loading && products.length === 0 && hasActiveFilters() && (
							<Box
								sx={{
									textAlign: "center",
									py: 8,
									px: 4,
									border: "2px dashed #e0e0e0",
									borderRadius: 2,
									backgroundColor: "#fafafa",
								}}>
								<Typography
									variant="h6"
									sx={{
										color: "#666",
										mb: 1,
										fontWeight: 500,
									}}>
									No products found
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: "#999",
										mb: 2,
									}}>
									Try adjusting your filters to see more results
								</Typography>
								<Button
									variant="outlined"
									size="small"
									onClick={() => {
										// Reset all filters
										const resetFilters = {
											key: "",
											department: "",
											subCategory: "",
											brand: "",
											subBrand: "",
											style: "",
											color: "",
											sleeve: "",
											division: "",
											season: "",
											fashionability: "",
											occasion: "",
											palette: "",
											fitType: "",
											fabricBlend: "",
											nod: "",
											channel: "",
											store: "",
											city: "",
											state: "",
											region: "",
											tier: "",
											aging: "",
											st_percentage: "",
											start_date: "",
											end_date: "",
										};
										setRampColorDrilldown(null);
										setBrokenFilter(null);
										setAvailableFilter(null);
										setFullSizeSetFilter(null);
										setSelectedSizeFilters([]);
										setSelectedSizeRatioFilters(new Set());
										setFilters(resetFilters);
										setPendingFilters(resetFilters);
										applyFilters();
									}}
									sx={{
										textTransform: "none",
										borderColor: "#ddd",
										color: "#666",
										"&:hover": {
											borderColor: "#999",
											backgroundColor: "#f5f5f5",
										},
									}}>
									Clear all filters
								</Button>
							</Box>
						)}

					{/* Pivotal / full-size quick-filter bars — horizontal */}
					{!loading && products.length > 0 && (
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-end",
								gap: 2,
								mb: 1.5,
								flexWrap: "wrap",
								rowGap: 1,
							}}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontSize: "0.72rem", color: "#555", fontWeight: 600, whiteSpace: "nowrap" }}>
									Pivotal Broken:
								</Typography>
								<ToggleButtonGroup
									value={brokenFilter}
									exclusive
									onChange={handleBrokenFilterChange}
									size="small"
									sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: "0.7rem", py: 0.4, px: 1.2, lineHeight: 1.4 } }}>
									<ToggleButton
										value="WH"
										sx={{
											borderColor: "#90caf9",
											color: "#64b5f6",
											"&.Mui-selected": { bgcolor: "#90caf9", color: "#fff", "&:hover": { bgcolor: "#64b5f6" } },
											"&:hover": { bgcolor: "#e3f2fd" },
										}}>
										⚠ WH Broken
										{whBrokenCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: brokenFilter === "WH" ? "rgba(255,255,255,0.35)" : "#90caf9", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{whBrokenCount}
											</Box>
										)}
									</ToggleButton>
									<ToggleButton
										value="EBO"
										sx={{
											borderColor: "#ef9a9a",
											color: "#e57373",
											"&.Mui-selected": { bgcolor: "#ef9a9a", color: "#fff", "&:hover": { bgcolor: "#e57373" } },
											"&:hover": { bgcolor: "#ffebee" },
										}}>
										⚠ EBO Broken
										{eboBrokenCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: brokenFilter === "EBO" ? "rgba(255,255,255,0.35)" : "#ef9a9a", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{eboBrokenCount}
											</Box>
										)}
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontSize: "0.72rem", color: "#555", fontWeight: 600, whiteSpace: "nowrap" }}>
									Pivotal Available:
								</Typography>
								<ToggleButtonGroup
									value={availableFilter}
									exclusive
									onChange={handleAvailableFilterChange}
									size="small"
									sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: "0.7rem", py: 0.4, px: 1.2, lineHeight: 1.4 } }}>
									<ToggleButton
										value="WH"
										sx={{
											borderColor: "#2e7d32",
											color: "#2e7d32",
											"&.Mui-selected": { bgcolor: "#2e7d32", color: "#fff", "&:hover": { bgcolor: "#1b5e20" } },
											"&:hover": { bgcolor: "#e8f5e9" },
										}}>
										✓ WH Available
										{whAvailableCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: availableFilter === "WH" ? "rgba(255,255,255,0.3)" : "#2e7d32", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{whAvailableCount}
											</Box>
										)}
									</ToggleButton>
									<ToggleButton
										value="EBO"
										sx={{
											borderColor: "#00695c",
											color: "#00695c",
											"&.Mui-selected": { bgcolor: "#00695c", color: "#fff", "&:hover": { bgcolor: "#004d40" } },
											"&:hover": { bgcolor: "#e0f2f1" },
										}}>
										✓ EBO Available
										{eboAvailableCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: availableFilter === "EBO" ? "rgba(255,255,255,0.3)" : "#00695c", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{eboAvailableCount}
											</Box>
										)}
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontSize: "0.72rem", color: "#555", fontWeight: 600, whiteSpace: "nowrap" }}>
									Full Size Set:
								</Typography>
								<ToggleButtonGroup
									value={fullSizeSetFilter}
									exclusive
									onChange={handleFullSizeSetFilterChange}
									size="small"
									sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: "0.7rem", py: 0.4, px: 1.2, lineHeight: 1.4 } }}>
									<ToggleButton
										value="WH"
										sx={{
											borderColor: "#6a1b9a",
											color: "#6a1b9a",
											"&.Mui-selected": { bgcolor: "#6a1b9a", color: "#fff", "&:hover": { bgcolor: "#4a148c" } },
											"&:hover": { bgcolor: "#f3e5f5" },
										}}>
										✓ WH Full Set
										{whFullSizeSetCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: fullSizeSetFilter === "WH" ? "rgba(255,255,255,0.3)" : "#6a1b9a", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{whFullSizeSetCount}
											</Box>
										)}
									</ToggleButton>
									<ToggleButton
										value="EBO"
										sx={{
											borderColor: "#e65100",
											color: "#e65100",
											"&.Mui-selected": { bgcolor: "#e65100", color: "#fff", "&:hover": { bgcolor: "#bf360c" } },
											"&:hover": { bgcolor: "#fff3e0" },
										}}>
										✓ EBO Full Set
										{eboFullSizeSetCount > 0 && (
											<Box component="span" sx={{ ml: 0.75, bgcolor: fullSizeSetFilter === "EBO" ? "rgba(255,255,255,0.3)" : "#e65100", color: "#fff", fontSize: "0.62rem", fontWeight: 700, borderRadius: 3, px: 0.6, py: 0.05 }}>
												{eboFullSizeSetCount}
											</Box>
										)}
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							{availableSizeOptions.length > 0 && (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, flex: "1 1 100%" }}>
									<Typography variant="body2" sx={{ fontSize: "0.72rem", color: "#555", fontWeight: 600, whiteSpace: "nowrap" }}>
										Sizes:
									</Typography>
									<ToggleButtonGroup
										value={selectedSizeFilters}
										onChange={handleSizeFilterChange}
										size="small"
										sx={{
											flexWrap: "wrap",
											gap: 0.5,
											"& .MuiToggleButtonGroup-grouped": {
												border: "1px solid #5c6bc0 !important",
												borderRadius: "16px !important",
												margin: 0,
											},
											"& .MuiToggleButton-root": {
												textTransform: "none",
												fontSize: "0.68rem",
												py: 0.35,
												px: 1,
												lineHeight: 1.4,
												borderColor: "#5c6bc0",
												color: "#3949ab",
											},
										}}>
										{availableSizeOptions.map(({ label, count }) => {
											const selected = selectedSizeFilters.includes(label);
											return (
												<ToggleButton
													key={label}
													value={label}
													sx={{
														"&.Mui-selected": {
															bgcolor: "#3949ab",
															color: "#fff",
															"&:hover": { bgcolor: "#283593" },
														},
														"&:hover": { bgcolor: "#e8eaf6" },
													}}>
													{label}
													{count > 0 && (
														<Box
															component="span"
															sx={{
																ml: 0.6,
																bgcolor: selected ? "rgba(255,255,255,0.3)" : "#3949ab",
																color: "#fff",
																fontSize: "0.58rem",
																fontWeight: 700,
																borderRadius: 3,
																px: 0.55,
																py: 0.05,
															}}>
															{count}
														</Box>
													)}
												</ToggleButton>
											);
										})}
									</ToggleButtonGroup>
								</Box>
							)}

							{(activeQuickFilter || activeSizeFilter || activeSizeRatioFilter) && (
								<Typography variant="body2" sx={{ fontSize: "0.68rem", color: "#888", pb: 0.5, ml: "auto", flex: "1 1 100%" }}>
									Showing {paginationSourceList.length} of {allProductsForView.length} options
									{quickFilterExportLabel ? ` · Export uses: ${quickFilterExportLabel}` : ""}
									{activeSizeFilter ? ` · Sizes: ${selectedSizeFilters.join(", ")}` : ""}
									{activeSizeRatioFilter ? ` · WH Ratio: ${Array.from(selectedSizeRatioFilters).join(" | ")}` : ""}
								</Typography>
							)}
						</Box>
					)}

					{/* Products Grid - This division will be captured for PDF */}
					<Box ref={pdfRef} sx={{ position: "relative" }}>
						<Fade in={!loading} timeout={120}>
							<Grid container spacing={1.5} justifyContent="flex-start" alignItems="flex-start" sx={{ minHeight: "400px" }}>
								{products.map((product, index) => (
									<Grid
										item
										key={`${product.PKEY}-${pagination.page}-${index}`}
										id={`pict-card-${String(product.PKEY).replace(/[^a-zA-Z0-9]/g, "-")}`}
										xs={12} sm={6} md={4} lg={3} xl={2.4}
										sx={{ scrollMarginTop: "80px", minWidth: 0 }}>
										<ProductCard
									product={product}
									metricsChannel={metricsChannel}
									allProducts={allProductsForView}
									onNavigateToProduct={navigateToProduct}
								/>
									</Grid>
								))}
								</Grid>
							</Fade>
							{rampGridSelectionOverlay && (
								<Box
									aria-hidden
									sx={{
										position: "absolute",
										inset: 0,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: "rgba(255, 255, 255, 0.58)",
										backdropFilter: "blur(2px)",
										zIndex: 2,
										borderRadius: 1,
										pointerEvents: "none",
									}}>
									<CircularProgress size={36} thickness={4} sx={{ color: "primary.main" }} />
								</Box>
							)}
						</Box>

						<Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
							<Pagination
								count={pagination.total_pages}
								page={pagination.page}
								onChange={handlePageChange}
								color="primary"
								size="large"
								siblingCount={2}
								showFirstButton
								showLastButton
							/>
						</Box>
					</Paper>
				)}
			</Container>

			<Dialog
				open={pdfFieldPickerOpen}
				onClose={handlePdfFieldPickerCancel}
				maxWidth="sm"
				fullWidth
				aria-labelledby="pdf-field-picker-title">
				<DialogTitle id="pdf-field-picker-title">
					{exportPickerFormat === "csv" ? "Export fields (CSV)" : "Export fields (PDF)"}
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
						{pdfFieldPickerScope === "current"
							? "Current page — choose metrics to include."
							: "All pages — same choices apply to every page."}
						{quickFilterExportLabel
							? ` Only products matching “${quickFilterExportLabel}” are exported.`
							: ""}
					</Typography>
				</DialogTitle>
				<DialogContent dividers>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
						{exportPickerFormat === "pdf"
							? "Page 1 is the cover (totals and applied filters). Product tiles start on page 2. Summary header applies to CSV only."
							: "Summary header adds Buy/Sold totals as leading rows. Product columns follow your selections."}
					</Typography>
					<Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap" }}>
						<Button size="small" variant="outlined" onClick={handlePdfFieldSelectAll}>
							Select all
						</Button>
						<Button size="small" variant="outlined" onClick={handlePdfFieldClearAll}>
							Clear all
						</Button>
					</Stack>
					<FormGroup sx={{ gap: 0.25 }}>
						{PICTORIAL_EXPORT_FIELDS.map(({ id, label }) => (
							<FormControlLabel
								key={id}
								control={
									<Checkbox
										size="small"
										checked={Boolean(pdfFieldSelection[id])}
										onChange={() => handlePdfFieldToggle(id)}
									/>
								}
								label={<Typography variant="body2">{label}</Typography>}
							/>
						))}
					</FormGroup>
					{exportPickerFormat === "csv" && (
						<>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mt: 2, mb: 0.75, fontWeight: 600 }}>
								Tooltip breakdowns (extra CSV columns — not included in PDF)
							</Typography>
							<FormGroup sx={{ gap: 0.25 }}>
								{PICTORIAL_CSV_TOOLTIP_FIELDS.map(({ id, label }) => (
									<FormControlLabel
										key={id}
										control={
											<Checkbox
												size="small"
												checked={Boolean(pdfFieldSelection[id])}
												onChange={() => handlePdfFieldToggle(id)}
											/>
										}
										label={<Typography variant="body2">{label}</Typography>}
									/>
								))}
							</FormGroup>
						</>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={handlePdfFieldPickerCancel} color="inherit">
						Cancel
					</Button>
					<Button variant="contained" onClick={handlePdfFieldPickerExport}>
						{exportPickerFormat === "csv" ? "Download CSV" : "Download PDF"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={uploadModalOpen}
				onClose={closeUploadModal}
				maxWidth="lg"
				fullWidth
				scroll="paper"
				PaperProps={{ sx: { maxHeight: "90vh" } }}>
				<UploadPage
					dateRange={dateRange}
					onBack={closeUploadModal}
					onApplyFilters={(filters, dateRangeToApply) => {
						handleApplyFiltersFromUpload(filters, dateRangeToApply);
						closeUploadModal();
					}}
				/>
			</Dialog>
		</ThemeProvider>
	);
};

export default PictorialST;
